/**
 * THE report contract. One definition, read by both sides.
 *
 * WHY THIS FILE HAS NO IMPORTS. The server validates an assembled report before
 * publishing it and the client validates the same bytes after reading them back.
 * Those were two hand-written shapes — a strict Zod schema in the edge function
 * and a `typeof x.forces === 'object'` guard in a React component — and they
 * disagreed. The assembler wrote `insufficientReason` on every body and the
 * server's own declared schemas rejected it; the client never checked at all and
 * cast with `as unknown as`. Every one of those is a shape nobody could have
 * verified from one place.
 *
 * So the contract is plain TypeScript with zero dependencies, which is the only
 * form both runtimes can import: Deno reads it from
 * `supabase/functions/_shared/strategic-analysis/`, Metro and Jest read it
 * through `@/lib/strategic-analysis`. There is no second copy to drift.
 *
 * KEYED BY THE EXACT PAIR. A parser is registered under
 * `(schema_version, analysis_type)` and looked up by exact string match. Not
 * "anything older than current", which assumes every past shape was a subset of
 * this one — the assumption that turns a corrupted report into a "legacy" one.
 * `1.0.0` is what the phase-117 release actually wrote (commit 647cdfad); those
 * readers are transcribed from that commit's report literals, field for field,
 * and are frozen. New shapes get a new version.
 *
 * THREE OUTCOMES, NEVER TWO. `valid`, `unsupported_version` and `malformed` are
 * distinct because they need different words on screen and different actions: a
 * version this build cannot read is a deployment problem the reader should be
 * told to wait out, and a malformed body is a corruption they should report.
 * Collapsing either into "no report" renders a published analysis as though the
 * founder had never generated one.
 *
 * EVERY MATERIAL DISPLAYED NODE CARRIES ITS OWN `claimKey`. That is the whole
 * reason 1.1.0 exists. The UI used to rebuild keys (`porter.${force}` against a
 * server key of `porter.${force}.${driver}`) and to match companies by prose
 * (`statement.includes(name)`), so citations attached to the wrong item or to
 * nothing at all. A key is now written by the assembler and read by the
 * component; neither invents one.
 */

// ===========================================================================
// Vocabularies
//
// Runtime arrays, because these values arrive from a database row. A TypeScript
// union vanishes at runtime and cannot check anything.
// ===========================================================================

export const ANALYSIS_TYPE_NAMES = [
  'competition',
  'porter',
  'pestel',
  'market_validation',
  'exit_strategy',
] as const;
export type AnalysisTypeName = (typeof ANALYSIS_TYPE_NAMES)[number];

export const COMPETITION_AXIS_NAMES = [
  'customer_scope',
  'solution_breadth',
  'delivery_intensity',
  'economic_position',
  'vertical_specialization',
  'deployment_depth',
] as const;
export type CompetitionAxisNameC = (typeof COMPETITION_AXIS_NAMES)[number];

export const COMPETITOR_CLASS_NAMES = ['direct', 'adjacent', 'substitute'] as const;
export const COMPETITION_OUTPUT_MODE_NAMES = ['quadrant', 'comparison_table_only'] as const;
export const COMPARISON_STATUS_NAMES = ['available', 'none', 'unavailable'] as const;
export const NOT_REASSESSED_REASONS = ['not_scheduled', 'budget_exhausted', 'provider_failed'] as const;

/**
 * How a company's identity was established. Carried INTO the report because the
 * reader is entitled to know whether "Acme GmbH" was matched on its own domain
 * or on a normalized name that two companies share.
 */
export const ENTITY_MATCH_METHOD_NAMES = [
  'provider_id',
  'registry_identity',
  'domain',
  'normalized_name_jurisdiction',
  'unresolved',
] as const;
export const MATCH_CONFIDENCE_NAMES = ['low', 'moderate', 'high'] as const;

/**
 * WHAT A MATERIAL NUMBER CAN BE MEASURED IN.
 *
 * `unit` was a free string on both sides, and the rule that a currency amount
 * needs an explicit currency was written as `unit === 'currency'` — a guard that
 * fires on ONE spelling. `unit: 'USD'` with no `currency` field passed it, which
 * is exactly the number the rule exists to reject: an amount of money that cannot
 * be compared to anything. So did `unit: 'bananas'`.
 *
 * Closed, so the currency rule becomes exact rather than a string match. Small on
 * purpose — a fourth unit is a decision about how the UI renders it, not a
 * vocabulary entry, since Market Validation currently formats every material
 * number as an amount.
 */
export const METRIC_UNIT_NAMES = ['currency', 'count', 'percent'] as const;
export type MetricUnit = (typeof METRIC_UNIT_NAMES)[number];

/**
 * HOW A MATERIAL NUMBER WAS ARRIVED AT.
 *
 * Rendered VERBATIM to a founder beside the figure it describes
 * (`MarketValidation.tsx`, under "Published base market"), so a free string let a
 * model print its own invented methodology as though it were one of ours —
 * "proprietary triangulation model" was accepted.
 *
 * `top_down` and `bottom_up` are the two the methodology already reasons about by
 * name (`market-validation.ts` types `usableMethod` as exactly those). `reported`
 * is the honest third: a published base-market figure whose source states the
 * number without disclosing how it was derived, which is the common case and
 * previously had nowhere truthful to go.
 */
export const DERIVATION_METHOD_NAMES = ['top_down', 'bottom_up', 'reported'] as const;
export type DerivationMethod = (typeof DERIVATION_METHOD_NAMES)[number];

export const PORTER_FORCE_NAMES = [
  'competitive_rivalry',
  'threat_of_new_entrants',
  'buyer_power',
  'supplier_power',
  'threat_of_substitutes',
] as const;
export const PORTER_RATING_NAMES = ['low', 'moderate', 'high', 'insufficient_evidence'] as const;

export const PESTEL_CATEGORY_NAMES = [
  'political',
  'economic',
  'social',
  'technological',
  'environmental',
  'legal',
] as const;
export const PESTEL_POSTURE_NAMES = ['favorable', 'adverse', 'mixed', 'insufficient_evidence'] as const;
export const PESTEL_DIRECTION_NAMES = ['opportunity', 'risk', 'mixed', 'unclear'] as const;
export const MATERIALITY_NAMES = ['low', 'moderate', 'high'] as const;
export const PESTEL_HORIZON_NAMES = ['current', '0_to_2_years', '3_to_5_years'] as const;
export const POINT_KIND_NAMES = ['question', 'signal_to_monitor', 'robust_action'] as const;

export const MARKET_CONCLUSION_NAMES = [
  'supported',
  'plausible_with_caveats',
  'potentially_overstated',
  'potentially_understated',
  'not_comparable',
  'insufficient_evidence',
] as const;

export const EXIT_PATHWAY_NAMES = [
  'strategic_acquisition',
  'private_equity_buyout',
  'merger',
  'secondary_sale',
  'ipo',
  'continued_independence',
] as const;
export const EXIT_PATHWAY_TIER_NAMES = [
  'supported',
  'plausible_with_caveats',
  'weakly_supported',
  'insufficient_evidence',
] as const;
export const ACQUIRER_FIT_TIER_NAMES = [
  'strong_strategic_fit',
  'plausible_candidate',
  'watchlist',
] as const;
export const ACQUIRER_FIT_DIMENSION_NAMES = [
  'product_use_case_adjacency',
  'customer_channel_adjacency',
  'geographic_compatibility',
  'acquisition_partnership_pattern',
  'capacity_integration_feasibility',
] as const;
export const THESIS_COMPARISON_NAMES = [
  'aligned',
  'partially_aligned',
  'divergent',
  'insufficient_evidence',
] as const;
export const PATHWAY_DRIVER_JUDGEMENT_NAMES = ['supports', 'mixed', 'weakens', 'unknown'] as const;

export const COUNTER_EVIDENCE_STATUS_NAMES = [
  'completed_with_results',
  'none_found_after_search',
  'provider_failed',
  'filter_not_supported',
  'budget_exhausted',
  'not_required',
] as const;

/**
 * What a displayed non-numeric statement IS.
 *
 * `evidence_backed` means observations were retained for it and a claim key
 * resolves. `ai_consideration` means a model produced it from the surrounding
 * material and nothing external verified it — a prompt, not a finding. There is
 * no third option, and there is deliberately no default: an unlabelled string
 * was how speculative "readiness gaps" and "milestones" reached investors
 * looking exactly like researched conclusions.
 */
export const STATEMENT_GROUNDS = ['evidence_backed', 'ai_consideration'] as const;
export type StatementGround = (typeof STATEMENT_GROUNDS)[number];

/**
 * A statement a reader sees, and where it came from.
 *
 * `claimKey` is present exactly when `ground` is `evidence_backed`. An
 * AI consideration has no claim because there is nothing to cite.
 */
export interface GroundedStatement {
  text: string;
  ground: StatementGround;
  claimKey?: string;
  evidenceIds?: string[];
}

// ===========================================================================
// Version registry
// ===========================================================================

/**
 * What a run published TODAY.
 *
 * 1.0.0 -> 1.1.0 because the persisted report SHAPE changed: every material
 * displayed node gained a `claimKey`, competition gained the historical
 * comparison fields and per-entity identity provenance, exit gained grounded
 * labelling on readiness gaps and milestones, and `insufficientReason` became a
 * declared field rather than an undeclared extra the strict schemas rejected.
 */
export const REPORT_SCHEMA_VERSION = '1.1.0';

/** Exact strings this build knows how to read. Order is oldest first. */
export const SUPPORTED_REPORT_SCHEMA_VERSIONS: readonly string[] = ['1.0.0', '1.1.0'];

export type ReportParseResult<T> =
  | { status: 'valid'; schemaVersion: string; body: T }
  /** A shape this build has no reader for. Never guessed at. */
  | { status: 'unsupported_version'; schemaVersion: string }
  /** A reader exists and the bytes failed it. `issues` are PATHS, never values. */
  | { status: 'malformed'; schemaVersion: string; issues: string[] };

// ===========================================================================
// 1.1.0 body types — what the assembler writes and every component renders
// ===========================================================================

export interface CompetitionEntityV11 {
  name: string;
  entityId: string;
  classification: (typeof COMPETITOR_CLASS_NAMES)[number];
  /** Integers 1..5 against an anchored rubric, keyed by registered axis. */
  positions: Record<string, number>;
  evidenceIds: string[];
  /** EXACT. The UI joins on this and never rebuilds it. */
  claimKey: string;
  discovery?: 'new' | 'reconfirmed';
  /** How this company's identity was actually established. Never assumed. */
  identity: {
    matchMethod: (typeof ENTITY_MATCH_METHOD_NAMES)[number];
    matchConfidence: (typeof MATCH_CONFIDENCE_NAMES)[number];
    domain?: string;
  };
}

export interface CompetitionAxisV11 {
  axis: CompetitionAxisNameC;
  lowLabel: string;
  highLabel: string;
  relevanceRationale?: string;
  /** An axis with no evidence for its relevance is a preference, not a finding. */
  evidenceIds: string[];
  claimKey: string;
}

export interface CompetitionBodyV11 {
  outputMode: (typeof COMPETITION_OUTPUT_MODE_NAMES)[number];
  axes: CompetitionAxisV11[];
  /**
   * The analysed startup's own coordinates.
   *
   * `founderSupplied` is the honest alternative to plotting the reader's own
   * company from evidence that does not exist: when the position comes from the
   * approved founder context rather than from retained sources, the map says so.
   */
  self:
    | {
      entityId?: string;
      positions: Record<string, number>;
      evidenceIds: string[];
      founderSupplied: boolean;
      claimKey: string;
    }
    | null;
  entities: CompetitionEntityV11[];
  notReFound: { entityId: string; name: string }[];
  notReassessed: {
    entityId: string;
    name: string;
    reason: (typeof NOT_REASSESSED_REASONS)[number];
  }[];
  comparisonStatus: (typeof COMPARISON_STATUS_NAMES)[number];
  fallbackReason: string | null;
  insufficientReason: string | null;
}

export interface PorterDriverV11 {
  driver: string;
  pressure: 0 | 1 | 2;
  /** The OBSERVED fact, separate from the interpretation below. */
  observation: string;
  interpretation: string;
  evidenceIds: string[];
  counterEvidenceStatus: (typeof COUNTER_EVIDENCE_STATUS_NAMES)[number];
  claimKey: string;
}

export interface PorterBodyV11 {
  forces: {
    force: (typeof PORTER_FORCE_NAMES)[number];
    rating: (typeof PORTER_RATING_NAMES)[number];
    drivers: PorterDriverV11[];
    supportedDriverCount: number;
    unknownDrivers: string[];
  }[];
  overallSynthesis: string;
  insufficientReason: string | null;
}

export interface PestelItemV11 {
  category: (typeof PESTEL_CATEGORY_NAMES)[number];
  summary: string;
  jurisdiction: string;
  direction: (typeof PESTEL_DIRECTION_NAMES)[number];
  materiality: (typeof MATERIALITY_NAMES)[number];
  horizon: (typeof PESTEL_HORIZON_NAMES)[number];
  affectedAssumption: string;
  /** From the SOURCE record, never from model prose. Absent when unknown. */
  publishedAt?: string;
  effectiveAt?: string;
  evidenceIds: string[];
  claimKey: string;
  /**
   * Present ONLY on a legal or political item citing no source this system can
   * confirm is authoritative.
   *
   * The item publishes and says so, rather than being deleted: refusing it turns
   * "we could not confirm this source is authoritative" into "this regulation
   * does not exist", and it assumes the host list is exhaustive when it is not.
   * Absent means the check passed or did not apply — never "we did not look".
   */
  sourceAuthority?: 'unverified';
}

export interface PestelBodyV11 {
  categories: {
    category: (typeof PESTEL_CATEGORY_NAMES)[number];
    posture: (typeof PESTEL_POSTURE_NAMES)[number];
    items: PestelItemV11[];
    claimKey: string;
  }[];
  /** Forward-looking by definition. Always labelled, never presented as fact. */
  pointsToConsider: {
    kind: (typeof POINT_KIND_NAMES)[number];
    text: string;
    relatedCategories: (typeof PESTEL_CATEGORY_NAMES)[number][];
  }[];
  insufficientReason: string | null;
}

export interface MaterialNumberC {
  value: number;
  unit: MetricUnit;
  currency?: string;
  geography: string;
  baseYear: number;
  method: DerivationMethod;
}

/**
 * The 1.0.0 shape: `unit` and `method` were free strings and the rows exist.
 *
 * A published report cannot be rewritten, so tightening the reader would make a
 * founder's existing analysis unopenable — the precise failure the versioned
 * contract boundary exists to prevent. 1.0.0 keeps the shape it was written
 * under; 1.1.0 gets the closed vocabulary. That the two now differ is the point
 * of having versions rather than one reader that must be true of both.
 */
export interface MaterialNumberLegacyC {
  value: number;
  unit: string;
  currency?: string;
  geography: string;
  baseYear: number;
  method: string;
}

export interface MarketRangeC {
  low: number;
  base: number;
  high: number;
}

export interface MarketValidationBodyV11 {
  /**
   * The definition comparison, FIELD BY FIELD. A boolean alone was derived from
   * "the model wrote no notes", which is a fact about the model.
   */
  definitionCompatible: boolean;
  definitionComparison: {
    field:
      | 'market_definition'
      | 'customer_unit'
      | 'geography'
      | 'currency'
      | 'base_year'
      | 'scope';
    founderValue: string | null;
    evidenceValue: string | null;
    compatible: boolean;
    note?: string;
  }[];
  definitionNotes: string[];
  topDown: {
    supported: boolean;
    base?: MaterialNumberC;
    /** Set when no candidate met the deterministic selection rules. */
    insufficientReason?: string;
    narrowingFactors: {
      label: string;
      low: number;
      base: number;
      high: number;
      evidenceIds: string[];
      founderProvided: boolean;
      claimKey: string;
    }[];
    range?: MarketRangeC;
    claimKey?: string;
  };
  /**
   * Bottom-up, from EVIDENCE. Never from the founder's own assumptions — the
   * previous build fed `marketAssumptions` straight in and printed the result
   * under "Independent", which made the founder's number corroborate itself.
   */
  bottomUp: {
    supported: boolean;
    /** `independent` is evidence-derived. `founder_stated` is the founder's own. */
    basis: 'independent' | 'founder_stated' | 'insufficient_evidence';
    tam?: MarketRangeC;
    sam?: MarketRangeC;
    som?: MarketRangeC;
    inputs: {
      label: string;
      low: number;
      base: number;
      high: number;
      evidenceIds: string[];
      founderProvided: boolean;
      claimKey: string;
    }[];
    insufficientReason?: string;
    claimKey?: string;
  };
  /** The founder's own figures, shown as a comparison and never as an input. */
  founderEstimate: { tam?: MarketRangeC; sam?: MarketRangeC; som?: MarketRangeC } | null;
  corroboratedRange: MarketRangeC | null;
  methodConflict: boolean;
  conclusion: (typeof MARKET_CONCLUSION_NAMES)[number];
  conclusionExplanation: string;
  comparisonClaimKey?: string;
  insufficientReason: string | null;
}

export interface AcquirerCandidateV11 {
  name: string;
  entityId: string;
  fitTier: (typeof ACQUIRER_FIT_TIER_NAMES)[number];
  dimensions: {
    dimension: (typeof ACQUIRER_FIT_DIMENSION_NAMES)[number];
    supported: boolean;
    rationale: string;
    evidenceIds: string[];
    ground: StatementGround;
  }[];
  evidenceIds: string[];
  /** REQUIRED, and keyed by entityId upstream — never by a mutable display name. */
  counterargument: GroundedStatement;
  evidenceGaps: string[];
  claimKey: string;
  identity: {
    matchMethod: (typeof ENTITY_MATCH_METHOD_NAMES)[number];
    matchConfidence: (typeof MATCH_CONFIDENCE_NAMES)[number];
    domain?: string;
  };
}

export interface ComparableTransactionV11 {
  buyer: string;
  target: string;
  announcedAt: string;
  rationale?: string;
  disclosedValue?: number;
  disclosedCurrency?: string;
  valueDisclosed: boolean;
  evidenceIds: string[];
  claimKey: string;
}

export interface ExitStrategyBodyV11 {
  pathways: {
    pathway: (typeof EXIT_PATHWAY_NAMES)[number];
    tier: (typeof EXIT_PATHWAY_TIER_NAMES)[number];
    drivers: {
      driver: string;
      judgement: (typeof PATHWAY_DRIVER_JUDGEMENT_NAMES)[number];
      rationale: string;
      evidenceIds: string[];
      claimKey: string;
      ground: StatementGround;
    }[];
    evidenceIds: string[];
    claimKey: string;
  }[];
  mostSupportedPathways: (typeof EXIT_PATHWAY_NAMES)[number][];
  candidates: AcquirerCandidateV11[];
  comparables: ComparableTransactionV11[];
  /** Labelled, one by one. An unlabelled gap reads as a researched finding. */
  readinessGaps: GroundedStatement[];
  milestones: GroundedStatement[];
  thesisComparison: (typeof THESIS_COMPARISON_NAMES)[number];
  assessedThesisVersionId: string;
  insufficientReason: string | null;
}

export type ReportBodyV11 =
  | CompetitionBodyV11
  | PorterBodyV11
  | PestelBodyV11
  | MarketValidationBodyV11
  | ExitStrategyBodyV11;

/** The body type for one analysis, at the CURRENT version. */
export type BodyForType<T extends AnalysisTypeName> = T extends 'competition' ? CompetitionBodyV11
  : T extends 'porter' ? PorterBodyV11
  : T extends 'pestel' ? PestelBodyV11
  : T extends 'market_validation' ? MarketValidationBodyV11
  : ExitStrategyBodyV11;

// ===========================================================================
// 1.0.0 body types
//
// TRANSCRIBED FROM COMMIT 647cdfad, which is the only code that has ever
// written a report at this version (it is the merge-base of `staging`). Fields
// this branch later added — `discovery`, `notReFound`, `notReassessed`,
// `comparisonStatus`, per-node `claimKey` — DO NOT APPEAR HERE, because no run
// has ever stored them under 1.0.0. Adding them "just in case" is how a
// corrupted row passes as legacy.
//
// FROZEN. Nothing may be added to these.
// ===========================================================================

/**
 * `1.0.0` IS THREE SHAPES, NOT ONE.
 *
 * `STRATEGIC_SCHEMA_VERSION` stayed at `1.0.0` while the competition writer
 * changed twice after the phase-117 release, so three structurally different
 * bodies can be sitting in the table under the same version string. Reading only
 * the first would declare the other two corrupt; accepting the union loosely
 * would let a genuinely corrupt row pass as "an old one".
 *
 * So each variant is its own strict reader, transcribed from the commit that
 * wrote it, and `readCompetitionV10` tries them in order:
 *
 *   A — 647cdfad, the phase-117 release.
 *       { outputMode, axes, self, entities, fallbackReason, insufficientReason }
 *
 *   B — 9f4af3f9, "stop the competitor set churning between runs".
 *       Adds `notReFound`, and every entity carries `discovery` unconditionally
 *       (`knownById.has(e.entityId) ? 'reconfirmed' : 'new'`).
 *
 *   C — 02f2d9ed, "competitive role comes from a report, not the registry",
 *       through 69569723. Adds `notReassessed` and `comparisonStatus`, and
 *       `discovery` becomes conditional on a checkable identity.
 *
 * The other four frameworks did not change their report literal anywhere in the
 * 1.0.0 window, which is why they have one reader each.
 */
export type CompetitionV10Variant = 'A' | 'B' | 'C';

export interface CompetitionBodyV10 {
  /** Which historical writer produced this row. Recorded, never guessed. */
  variant: CompetitionV10Variant;
  outputMode: (typeof COMPETITION_OUTPUT_MODE_NAMES)[number];
  axes: {
    axis: CompetitionAxisNameC;
    lowLabel: string;
    highLabel: string;
    relevanceRationale?: string;
  }[];
  self?: { positions: Record<string, number> } | null;
  entities: {
    name: string;
    entityId: string;
    classification: (typeof COMPETITOR_CLASS_NAMES)[number];
    positions: Record<string, number>;
    evidenceIds: string[];
    /** Present from variant B onward. */
    discovery?: 'new' | 'reconfirmed';
  }[];
  /** Present from variant B onward. */
  notReFound?: { entityId: string; name: string }[];
  /** Present from variant C onward. */
  notReassessed?: {
    entityId: string;
    name: string;
    reason: (typeof NOT_REASSESSED_REASONS)[number];
  }[];
  /** Present from variant C onward. */
  comparisonStatus?: (typeof COMPARISON_STATUS_NAMES)[number];
  fallbackReason: string | null;
  insufficientReason: string | null;
}

export interface PorterBodyV10 {
  forces: {
    force: (typeof PORTER_FORCE_NAMES)[number];
    rating: (typeof PORTER_RATING_NAMES)[number];
    drivers: {
      driver: string;
      pressure: 0 | 1 | 2;
      observation: string;
      interpretation: string;
      evidenceIds: string[];
      counterEvidenceStatus: (typeof COUNTER_EVIDENCE_STATUS_NAMES)[number];
    }[];
    supportedDriverCount: number;
    unknownDrivers: string[];
  }[];
  overallSynthesis: string;
  insufficientReason: string | null;
}

export interface PestelBodyV10 {
  categories: {
    category: (typeof PESTEL_CATEGORY_NAMES)[number];
    posture: (typeof PESTEL_POSTURE_NAMES)[number];
    items: {
      category: (typeof PESTEL_CATEGORY_NAMES)[number];
      summary: string;
      jurisdiction: string;
      direction: (typeof PESTEL_DIRECTION_NAMES)[number];
      materiality: (typeof MATERIALITY_NAMES)[number];
      horizon: (typeof PESTEL_HORIZON_NAMES)[number];
      affectedAssumption: string;
      publishedAt?: string;
      effectiveAt?: string;
      evidenceIds: string[];
    }[];
  }[];
  pointsToConsider: {
    kind: (typeof POINT_KIND_NAMES)[number];
    text: string;
    /**
     * REQUIRED at 1.0.0. `pestelSynthesisSchema` made it required on the model
     * response and `assemblePestel` copied the validated list through verbatim,
     * so every stored 1.0.0 point carries it. Making it optional here would be
     * an invented history.
     */
    relatedCategories: (typeof PESTEL_CATEGORY_NAMES)[number][];
  }[];
  insufficientReason: string | null;
}

export interface MarketValidationBodyV10 {
  definitionCompatible: boolean;
  definitionNotes: string[];
  topDown: {
    supported: boolean;
    base?: MaterialNumberLegacyC;
    narrowingFactors: {
      label: string;
      low: number;
      base: number;
      high: number;
      evidenceIds: string[];
      founderProvided: boolean;
    }[];
    range?: MarketRangeC;
  };
  bottomUp: { supported: boolean; tam?: MarketRangeC; sam?: MarketRangeC; som?: MarketRangeC };
  corroboratedRange: MarketRangeC | null;
  methodConflict: boolean;
  conclusion: (typeof MARKET_CONCLUSION_NAMES)[number];
  conclusionExplanation: string;
  insufficientReason: string | null;
}

export interface ExitStrategyBodyV10 {
  pathways: {
    pathway: (typeof EXIT_PATHWAY_NAMES)[number];
    tier: (typeof EXIT_PATHWAY_TIER_NAMES)[number];
    drivers: {
      driver: string;
      judgement: (typeof PATHWAY_DRIVER_JUDGEMENT_NAMES)[number];
      rationale: string;
      evidenceIds: string[];
    }[];
    evidenceIds: string[];
  }[];
  mostSupportedPathways: (typeof EXIT_PATHWAY_NAMES)[number][];
  candidates: {
    name: string;
    entityId: string;
    fitTier: (typeof ACQUIRER_FIT_TIER_NAMES)[number];
    dimensions: {
      dimension: (typeof ACQUIRER_FIT_DIMENSION_NAMES)[number];
      supported: boolean;
      rationale: string;
      evidenceIds: string[];
    }[];
    evidenceIds: string[];
    counterargument: string;
    evidenceGaps?: string[];
  }[];
  comparables: {
    buyer: string;
    target: string;
    announcedAt: string;
    rationale?: string;
    disclosedValue?: number;
    disclosedCurrency?: string;
    valueDisclosed: boolean;
    evidenceIds: string[];
  }[];
  readinessGaps: string[];
  milestones: string[];
  thesisComparison: (typeof THESIS_COMPARISON_NAMES)[number];
  assessedThesisVersionId: string;
  insufficientReason: string | null;
}

// ===========================================================================
// Validator toolkit
//
// Deliberately tiny and explicit. Every helper records a PATH and a reason and
// returns null, so one pass collects every problem instead of throwing on the
// first — a report that fails four ways should say so once.
// ===========================================================================

class Issues {
  readonly list: string[] = [];
  add(path: string, message: string): null {
    // Paths and reasons only. The VALUE is stored company material and model
    // output; it must never travel into a log or an error body.
    if (this.list.length < 24) this.list.push(`${path}: ${message}`);
    return null;
  }
  get ok(): boolean {
    return this.list.length === 0;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function rec(value: unknown, path: string, is: Issues): Record<string, unknown> | null {
  return isRecord(value) ? value : is.add(path, 'expected an object');
}

/**
 * Unknown keys are an ERROR, not extra data.
 *
 * This is what makes the product exclusions structural: a body carrying
 * `probability` on an acquirer, or `meanScore` beside a Porter band, fails here
 * rather than reaching a component that might render it.
 */
function strict(
  value: Record<string, unknown>,
  allowed: readonly string[],
  path: string,
  is: Issues,
): void {
  for (const key of Object.keys(value)) {
    if (!allowed.includes(key)) is.add(`${path}.${key}`, 'unrecognized key');
  }
}

function str(value: unknown, path: string, is: Issues): string | null {
  if (typeof value !== 'string' || value.length === 0) return is.add(path, 'expected a non-empty string');
  return value;
}

/** Present AND not blank. `'   '` is not an identity a run could have written. */
function ident(value: unknown, path: string, is: Issues): string | null {
  if (typeof value !== 'string' || value.trim().length === 0) {
    return is.add(path, 'expected a non-blank identifier');
  }
  return value;
}

function optStr(value: unknown, path: string, is: Issues): string | undefined | null {
  if (value === undefined) return undefined;
  return str(value, path, is) ?? null;
}

function nullableStr(value: unknown, path: string, is: Issues): string | null | undefined {
  if (value === null) return null;
  if (typeof value === 'string' && value.length > 0) return value;
  return is.add(path, 'expected a non-empty string or null') ?? undefined;
}

function bool(value: unknown, path: string, is: Issues): boolean | null {
  if (typeof value !== 'boolean') return is.add(path, 'expected a boolean');
  return value;
}

function fin(value: unknown, path: string, is: Issues): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return is.add(path, 'expected a finite number');
  return value;
}

function int(value: unknown, path: string, is: Issues, min: number, max: number): number | null {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < min || value > max) {
    return is.add(path, `expected an integer in [${min}, ${max}]`);
  }
  return value;
}

function arr(value: unknown, path: string, is: Issues): unknown[] | null {
  if (!Array.isArray(value)) return is.add(path, 'expected an array');
  return value;
}

function oneOf<T extends string>(
  value: unknown,
  allowed: readonly T[],
  path: string,
  is: Issues,
): T | null {
  if (typeof value !== 'string' || !(allowed as readonly string[]).includes(value)) {
    return is.add(path, 'not a registered value');
  }
  return value as T;
}

function strArray(value: unknown, path: string, is: Issues, minLength = 0): string[] | null {
  const list = arr(value, path, is);
  if (!list) return null;
  const out: string[] = [];
  for (let i = 0; i < list.length; i++) {
    const item = str(list[i], `${path}.${i}`, is);
    if (item !== null) out.push(item);
  }
  if (out.length < minLength) return is.add(path, `expected at least ${minLength} entries`);
  return out;
}

function enumArray<T extends string>(
  value: unknown,
  allowed: readonly T[],
  path: string,
  is: Issues,
): T[] | null {
  const list = arr(value, path, is);
  if (!list) return null;
  const out: T[] = [];
  for (let i = 0; i < list.length; i++) {
    const item = oneOf(list[i], allowed, `${path}.${i}`, is);
    if (item !== null) out.push(item);
  }
  return out;
}

/**
 * Coordinates on an anchored rubric.
 *
 * Keys must be REGISTERED axes and values integers 1..5. A decimal here would be
 * false precision on a five-position rubric, and an unregistered key would put a
 * point on an axis nobody can explain.
 */
function positions(value: unknown, path: string, is: Issues): Record<string, number> | null {
  const map = rec(value, path, is);
  if (!map) return null;
  const out: Record<string, number> = {};
  for (const [key, raw] of Object.entries(map)) {
    if (!(COMPETITION_AXIS_NAMES as readonly string[]).includes(key)) {
      is.add(`${path}.${key}`, 'not a registered axis');
      continue;
    }
    const position = int(raw, `${path}.${key}`, is, 1, 5);
    if (position !== null) out[key] = position;
  }
  return out;
}

function range(value: unknown, path: string, is: Issues): MarketRangeC | null {
  const map = rec(value, path, is);
  if (!map) return null;
  strict(map, ['low', 'base', 'high'], path, is);
  const low = fin(map.low, `${path}.low`, is);
  const base = fin(map.base, `${path}.base`, is);
  const high = fin(map.high, `${path}.high`, is);
  if (low === null || base === null || high === null) return null;
  if (!(low <= base && base <= high)) return is.add(path, 'bounds must be ordered low <= base <= high');
  return { low, base, high };
}

function optRange(value: unknown, path: string, is: Issues): MarketRangeC | undefined | null {
  if (value === undefined) return undefined;
  return range(value, path, is);
}

function materialNumber(value: unknown, path: string, is: Issues): MaterialNumberC | null {
  const map = rec(value, path, is);
  if (!map) return null;
  strict(map, ['value', 'unit', 'currency', 'geography', 'baseYear', 'method'], path, is);
  const parsed = {
    value: fin(map.value, `${path}.value`, is),
    unit: oneOf(map.unit, METRIC_UNIT_NAMES, `${path}.unit`, is),
    currency: optStr(map.currency, `${path}.currency`, is),
    geography: str(map.geography, `${path}.geography`, is),
    baseYear: int(map.baseYear, `${path}.baseYear`, is, 1900, 2200),
    method: oneOf(map.method, DERIVATION_METHOD_NAMES, `${path}.method`, is),
  };
  if (
    parsed.value === null || parsed.unit === null || parsed.geography === null ||
    parsed.baseYear === null || parsed.method === null || parsed.currency === null
  ) return null;
  // A currency amount with no currency cannot be compared to anything, which is
  // the whole job of a material number. EXACT now that the vocabulary is closed:
  // this used to be a string match, so `unit: 'USD'` with no currency slipped
  // past the very rule written to catch it.
  if (parsed.unit === 'currency' && parsed.currency === undefined) {
    return is.add(`${path}.currency`, 'a currency amount requires an explicit currency');
  }
  return {
    value: parsed.value,
    unit: parsed.unit,
    ...(parsed.currency === undefined ? {} : { currency: parsed.currency }),
    geography: parsed.geography,
    baseYear: parsed.baseYear,
    method: parsed.method,
  };
}

/**
 * The 1.0.0 material number: same fields, free-string `unit` and `method`.
 *
 * Deliberately a SEPARATE function rather than a flag on the strict one. A
 * permissive mode threaded through the strict decoder is one `if` away from being
 * reachable by a 1.1.0 body, and the whole value of the vocabulary is that a
 * current report cannot carry a unit or a method the server does not own.
 *
 * The currency rule still applies: it predates the vocabulary and was always
 * about the number being comparable, which is as true of a legacy row as a new
 * one.
 */
function legacyMaterialNumber(
  value: unknown,
  path: string,
  is: Issues,
): MaterialNumberLegacyC | null {
  const map = rec(value, path, is);
  if (!map) return null;
  strict(map, ['value', 'unit', 'currency', 'geography', 'baseYear', 'method'], path, is);
  const parsed = {
    value: fin(map.value, `${path}.value`, is),
    unit: str(map.unit, `${path}.unit`, is),
    currency: optStr(map.currency, `${path}.currency`, is),
    geography: str(map.geography, `${path}.geography`, is),
    baseYear: int(map.baseYear, `${path}.baseYear`, is, 1900, 2200),
    method: str(map.method, `${path}.method`, is),
  };
  if (
    parsed.value === null || parsed.unit === null || parsed.geography === null ||
    parsed.baseYear === null || parsed.method === null || parsed.currency === null
  ) return null;
  if (parsed.unit === 'currency' && parsed.currency === undefined) {
    return is.add(`${path}.currency`, 'a currency amount requires an explicit currency');
  }
  return {
    value: parsed.value,
    unit: parsed.unit,
    ...(parsed.currency === undefined ? {} : { currency: parsed.currency }),
    geography: parsed.geography,
    baseYear: parsed.baseYear,
    method: parsed.method,
  };
}

function identityBlock(value: unknown, path: string, is: Issues) {
  const map = rec(value, path, is);
  if (!map) return null;
  strict(map, ['matchMethod', 'matchConfidence', 'domain'], path, is);
  const matchMethod = oneOf(map.matchMethod, ENTITY_MATCH_METHOD_NAMES, `${path}.matchMethod`, is);
  const matchConfidence = oneOf(map.matchConfidence, MATCH_CONFIDENCE_NAMES, `${path}.matchConfidence`, is);
  const domain = optStr(map.domain, `${path}.domain`, is);
  if (matchMethod === null || matchConfidence === null || domain === null) return null;
  // A domain match without a domain is a claim about how the identity was
  // established that the record itself contradicts.
  if (matchMethod === 'domain' && domain === undefined) {
    return is.add(`${path}.domain`, 'a domain match requires the domain it matched on');
  }
  // Nothing checkable was established, so nothing may be asserted confidently.
  if (matchMethod === 'unresolved' && matchConfidence === 'high') {
    return is.add(`${path}.matchConfidence`, 'an unresolved identity cannot be high confidence');
  }
  return {
    matchMethod,
    matchConfidence,
    ...(domain === undefined ? {} : { domain }),
  };
}

function groundedStatement(value: unknown, path: string, is: Issues): GroundedStatement | null {
  const map = rec(value, path, is);
  if (!map) return null;
  strict(map, ['text', 'ground', 'claimKey', 'evidenceIds'], path, is);
  const text = str(map.text, `${path}.text`, is);
  const ground = oneOf(map.ground, STATEMENT_GROUNDS, `${path}.ground`, is);
  const claimKey = optStr(map.claimKey, `${path}.claimKey`, is);
  const evidenceIds = map.evidenceIds === undefined
    ? undefined
    : strArray(map.evidenceIds, `${path}.evidenceIds`, is);
  if (text === null || ground === null || claimKey === null || evidenceIds === null) return null;
  // The rule this type exists for: evidence-backed means there is something to
  // open, and an AI consideration must not carry a citation it cannot support.
  if (ground === 'evidence_backed' && (claimKey === undefined || (evidenceIds ?? []).length === 0)) {
    return is.add(path, 'an evidence_backed statement requires a claimKey and at least one evidence id');
  }
  if (ground === 'ai_consideration' && claimKey !== undefined) {
    return is.add(`${path}.claimKey`, 'an ai_consideration has no claim to cite');
  }
  return {
    text,
    ground,
    ...(claimKey === undefined ? {} : { claimKey }),
    ...(evidenceIds === undefined ? {} : { evidenceIds }),
  };
}

/** Every claim key in a report must be unique — the table enforces it too. */
function assertUniqueKeys(keys: readonly string[], path: string, is: Issues): void {
  const seen = new Set<string>();
  for (const key of keys) {
    if (seen.has(key)) is.add(path, 'duplicate claim key');
    seen.add(key);
  }
}

// ===========================================================================
// 1.1.0 readers
// ===========================================================================

/**
 * The minimum comparable set a positioning quadrant may be drawn from.
 *
 * Mirrors `methodology/competition.ts`. Kept here as well because this file is
 * the contract a stored report is checked against, and a quadrant published
 * with two points is a different answer, not a smaller one.
 */
export const MIN_QUADRANT_ENTITIES = 3;

function readCompetitionV11(value: unknown, is: Issues): CompetitionBodyV11 | null {
  const body = rec(value, 'report', is);
  if (!body) return null;
  strict(body, [
    'outputMode',
    'axes',
    'self',
    'entities',
    'notReFound',
    'notReassessed',
    'comparisonStatus',
    'fallbackReason',
    'insufficientReason',
  ], 'report', is);

  const outputMode = oneOf(body.outputMode, COMPETITION_OUTPUT_MODE_NAMES, 'report.outputMode', is);
  const comparisonStatus = oneOf(body.comparisonStatus, COMPARISON_STATUS_NAMES, 'report.comparisonStatus', is);
  const fallbackReason = nullableStr(body.fallbackReason, 'report.fallbackReason', is);
  const insufficientReason = nullableStr(body.insufficientReason, 'report.insufficientReason', is);

  const rawAxes = arr(body.axes, 'report.axes', is);
  const axes: CompetitionAxisV11[] = [];
  for (let i = 0; i < (rawAxes ?? []).length; i++) {
    const path = `report.axes.${i}`;
    const entry = rec(rawAxes![i], path, is);
    if (!entry) continue;
    strict(entry, ['axis', 'lowLabel', 'highLabel', 'relevanceRationale', 'evidenceIds', 'claimKey'], path, is);
    const axis = oneOf(entry.axis, COMPETITION_AXIS_NAMES, `${path}.axis`, is);
    const lowLabel = str(entry.lowLabel, `${path}.lowLabel`, is);
    const highLabel = str(entry.highLabel, `${path}.highLabel`, is);
    const relevanceRationale = optStr(entry.relevanceRationale, `${path}.relevanceRationale`, is);
    const evidenceIds = strArray(entry.evidenceIds, `${path}.evidenceIds`, is, 1);
    const claimKey = ident(entry.claimKey, `${path}.claimKey`, is);
    if (
      axis === null || lowLabel === null || highLabel === null ||
      relevanceRationale === null || evidenceIds === null || claimKey === null
    ) continue;
    axes.push({
      axis,
      lowLabel,
      highLabel,
      ...(relevanceRationale === undefined ? {} : { relevanceRationale }),
      evidenceIds,
      claimKey,
    });
  }

  const rawEntities = arr(body.entities, 'report.entities', is);
  const entities: CompetitionEntityV11[] = [];
  for (let i = 0; i < (rawEntities ?? []).length; i++) {
    const path = `report.entities.${i}`;
    const entry = rec(rawEntities![i], path, is);
    if (!entry) continue;
    strict(entry, [
      'name',
      'entityId',
      'classification',
      'positions',
      'evidenceIds',
      'claimKey',
      'discovery',
      'identity',
    ], path, is);
    const name = ident(entry.name, `${path}.name`, is);
    const entityId = ident(entry.entityId, `${path}.entityId`, is);
    const classification = oneOf(entry.classification, COMPETITOR_CLASS_NAMES, `${path}.classification`, is);
    const pos = positions(entry.positions, `${path}.positions`, is);
    const evidenceIds = strArray(entry.evidenceIds, `${path}.evidenceIds`, is, 1);
    const claimKey = ident(entry.claimKey, `${path}.claimKey`, is);
    const discovery = entry.discovery === undefined
      ? undefined
      : oneOf(entry.discovery, ['new', 'reconfirmed'] as const, `${path}.discovery`, is);
    const identity = identityBlock(entry.identity, `${path}.identity`, is);
    if (
      name === null || entityId === null || classification === null || pos === null ||
      evidenceIds === null || claimKey === null || discovery === null || identity === null
    ) continue;
    entities.push({
      name,
      entityId,
      classification,
      positions: pos,
      evidenceIds,
      claimKey,
      ...(discovery === undefined ? {} : { discovery }),
      identity,
    });
  }

  let self: CompetitionBodyV11['self'] = null;
  if (body.self !== null && body.self !== undefined) {
    const path = 'report.self';
    const entry = rec(body.self, path, is);
    if (entry) {
      strict(entry, ['entityId', 'positions', 'evidenceIds', 'founderSupplied', 'claimKey'], path, is);
      const entityId = entry.entityId === undefined ? undefined : ident(entry.entityId, `${path}.entityId`, is);
      const pos = positions(entry.positions, `${path}.positions`, is);
      const evidenceIds = strArray(entry.evidenceIds, `${path}.evidenceIds`, is);
      const founderSupplied = bool(entry.founderSupplied, `${path}.founderSupplied`, is);
      const claimKey = ident(entry.claimKey, `${path}.claimKey`, is);
      if (pos !== null && evidenceIds !== null && founderSupplied !== null && claimKey !== null && entityId !== null) {
        // Evidence or an explicit founder attribution. A plotted position with
        // neither states a coordinate nobody stands behind.
        if (evidenceIds.length === 0 && !founderSupplied) {
          is.add(`${path}.evidenceIds`, 'the startup position needs evidence or an explicit founder attribution');
        }
        self = {
          ...(entityId === undefined ? {} : { entityId }),
          positions: pos,
          evidenceIds,
          founderSupplied,
          claimKey,
        };
      }
    }
  }

  const notReFound: CompetitionBodyV11['notReFound'] = [];
  for (const [i, raw] of (arr(body.notReFound, 'report.notReFound', is) ?? []).entries()) {
    const path = `report.notReFound.${i}`;
    const entry = rec(raw, path, is);
    if (!entry) continue;
    strict(entry, ['entityId', 'name'], path, is);
    const entityId = ident(entry.entityId, `${path}.entityId`, is);
    const name = ident(entry.name, `${path}.name`, is);
    if (entityId !== null && name !== null) notReFound.push({ entityId, name });
  }

  const notReassessed: CompetitionBodyV11['notReassessed'] = [];
  for (const [i, raw] of (arr(body.notReassessed, 'report.notReassessed', is) ?? []).entries()) {
    const path = `report.notReassessed.${i}`;
    const entry = rec(raw, path, is);
    if (!entry) continue;
    strict(entry, ['entityId', 'name', 'reason'], path, is);
    const entityId = ident(entry.entityId, `${path}.entityId`, is);
    const name = ident(entry.name, `${path}.name`, is);
    const reason = oneOf(entry.reason, NOT_REASSESSED_REASONS, `${path}.reason`, is);
    if (entityId !== null && name !== null && reason !== null) notReassessed.push({ entityId, name, reason });
  }

  if (
    outputMode === null || comparisonStatus === null ||
    fallbackReason === undefined || insufficientReason === undefined
  ) return null;

  // ---- discriminated invariants -------------------------------------------
  //
  // Each of these has a failure it exists to stop, and every one of them was
  // reachable: the previous stored-shape schema accepted a quadrant with no
  // axes, a comparison table carrying axes and a `self`, an entity list that was
  // empty while `insufficientReason` was null, and a self identity that
  // duplicated a competitor's.
  if (outputMode === 'quadrant') {
    if (axes.length !== 2) is.add('report.axes', 'a quadrant requires exactly two axes');
    if (axes.length === 2 && axes[0].axis === axes[1].axis) {
      is.add('report.axes', 'a quadrant requires two distinct axes');
    }
    if (fallbackReason !== null) is.add('report.fallbackReason', 'a published quadrant has no fallback reason');
    if (!self) is.add('report.self', 'a quadrant requires the analysed startup to be plotted');
    if (entities.length < MIN_QUADRANT_ENTITIES) {
      is.add('report.entities', `a quadrant requires at least ${MIN_QUADRANT_ENTITIES} comparable entities`);
    }
    for (const axis of axes) {
      if (self && self.positions[axis.axis] === undefined) {
        is.add('report.self.positions', 'the startup lacks a coordinate on a published axis');
      }
      for (const [i, entity] of entities.entries()) {
        if (entity.positions[axis.axis] === undefined) {
          is.add(`report.entities.${i}.positions`, 'missing a coordinate on a published axis');
        }
      }
    }
  } else {
    if (axes.length !== 0) is.add('report.axes', 'a comparison table publishes no axes');
    if (self !== null) is.add('report.self', 'a comparison table does not plot the startup');
    if (fallbackReason === null) {
      is.add('report.fallbackReason', 'a comparison table must say why the quadrant was withheld');
    }
  }

  if (insufficientReason !== null && entities.length > 0) {
    is.add('report.insufficientReason', 'a report with entities is not insufficient');
  }
  if (insufficientReason === null && entities.length === 0) {
    is.add('report.entities', 'an empty entity set must state why it is insufficient');
  }

  const entityIds = new Set(entities.map((e) => e.entityId));
  if (entityIds.size !== entities.length) is.add('report.entities', 'duplicate entityId');
  if (self?.entityId && entityIds.has(self.entityId)) {
    is.add('report.self.entityId', 'the analysed startup cannot also be one of its competitors');
  }
  if (comparisonStatus !== 'available' && (notReFound.length > 0 || notReassessed.length > 0)) {
    is.add('report.comparisonStatus', 'comparison lists require an available comparison');
  }
  assertUniqueKeys(
    [...axes.map((a) => a.claimKey), ...entities.map((e) => e.claimKey), ...(self ? [self.claimKey] : [])],
    'report',
    is,
  );

  if (!is.ok) return null;
  return {
    outputMode,
    axes,
    self,
    entities,
    notReFound,
    notReassessed,
    comparisonStatus,
    fallbackReason,
    insufficientReason,
  };
}

function readPorterV11(value: unknown, is: Issues): PorterBodyV11 | null {
  const body = rec(value, 'report', is);
  if (!body) return null;
  strict(body, ['forces', 'overallSynthesis', 'insufficientReason'], 'report', is);

  const overallSynthesis = str(body.overallSynthesis, 'report.overallSynthesis', is);
  const insufficientReason = nullableStr(body.insufficientReason, 'report.insufficientReason', is);
  const rawForces = arr(body.forces, 'report.forces', is);

  const forces: PorterBodyV11['forces'] = [];
  for (let i = 0; i < (rawForces ?? []).length; i++) {
    const path = `report.forces.${i}`;
    const entry = rec(rawForces![i], path, is);
    if (!entry) continue;
    strict(entry, ['force', 'rating', 'drivers', 'supportedDriverCount', 'unknownDrivers'], path, is);
    const force = oneOf(entry.force, PORTER_FORCE_NAMES, `${path}.force`, is);
    const rating = oneOf(entry.rating, PORTER_RATING_NAMES, `${path}.rating`, is);
    const supportedDriverCount = int(entry.supportedDriverCount, `${path}.supportedDriverCount`, is, 0, 64);
    const unknownDrivers = strArray(entry.unknownDrivers, `${path}.unknownDrivers`, is);
    const rawDrivers = arr(entry.drivers, `${path}.drivers`, is);

    const drivers: PorterDriverV11[] = [];
    for (let j = 0; j < (rawDrivers ?? []).length; j++) {
      const dPath = `${path}.drivers.${j}`;
      const driverEntry = rec(rawDrivers![j], dPath, is);
      if (!driverEntry) continue;
      strict(driverEntry, [
        'driver',
        'pressure',
        'observation',
        'interpretation',
        'evidenceIds',
        'counterEvidenceStatus',
        'claimKey',
      ], dPath, is);
      const driver = ident(driverEntry.driver, `${dPath}.driver`, is);
      const pressure = int(driverEntry.pressure, `${dPath}.pressure`, is, 0, 2);
      const observation = str(driverEntry.observation, `${dPath}.observation`, is);
      const interpretation = str(driverEntry.interpretation, `${dPath}.interpretation`, is);
      const evidenceIds = strArray(driverEntry.evidenceIds, `${dPath}.evidenceIds`, is, 1);
      const counterEvidenceStatus = oneOf(
        driverEntry.counterEvidenceStatus,
        COUNTER_EVIDENCE_STATUS_NAMES,
        `${dPath}.counterEvidenceStatus`,
        is,
      );
      const claimKey = ident(driverEntry.claimKey, `${dPath}.claimKey`, is);
      if (
        driver === null || pressure === null || observation === null || interpretation === null ||
        evidenceIds === null || counterEvidenceStatus === null || claimKey === null || force === null
      ) continue;
      // The key the assembler writes and the UI reads. Stated here so a drift
      // between the two fails the contract rather than the citation.
      if (claimKey !== `porter.${force}.${driver}`) {
        is.add(`${dPath}.claimKey`, 'claim key does not match its force and driver');
      }
      drivers.push({
        driver,
        pressure: pressure as 0 | 1 | 2,
        observation,
        interpretation,
        evidenceIds,
        counterEvidenceStatus,
        claimKey,
      });
    }

    if (
      force === null || rating === null || supportedDriverCount === null || unknownDrivers === null
    ) continue;
    // The count is the accepted-driver count. A rating derived from a different
    // number than the one displayed is unreproducible.
    if (supportedDriverCount !== drivers.length) {
      is.add(`${path}.supportedDriverCount`, 'must equal the number of accepted drivers');
    }
    if (rating === 'insufficient_evidence' && drivers.length > 0) {
      is.add(`${path}.rating`, 'a force with accepted drivers is not insufficient');
    }
    if (rating !== 'insufficient_evidence' && drivers.length === 0) {
      is.add(`${path}.rating`, 'a force with no accepted driver cannot be rated');
    }
    forces.push({ force, rating, drivers, supportedDriverCount, unknownDrivers });
  }

  if (overallSynthesis === null || insufficientReason === undefined) return null;

  const rated = forces.filter((f) => f.rating !== 'insufficient_evidence').length;
  if (insufficientReason === null && rated === 0) {
    is.add('report.insufficientReason', 'no rated force must state why the report is insufficient');
  }
  if (insufficientReason !== null && rated > 0) {
    is.add('report.insufficientReason', 'a report with a rated force is not insufficient');
  }
  if (new Set(forces.map((f) => f.force)).size !== forces.length) {
    is.add('report.forces', 'duplicate force');
  }
  assertUniqueKeys(forces.flatMap((f) => f.drivers.map((d) => d.claimKey)), 'report.forces', is);

  if (!is.ok) return null;
  return { forces, overallSynthesis, insufficientReason };
}

function readPestelItemV11(
  value: unknown,
  path: string,
  is: Issues,
  expectedCategory: (typeof PESTEL_CATEGORY_NAMES)[number] | null,
): PestelItemV11 | null {
  const entry = rec(value, path, is);
  if (!entry) return null;
  strict(entry, [
    'category',
    'summary',
    'jurisdiction',
    'direction',
    'materiality',
    'horizon',
    'affectedAssumption',
    'publishedAt',
    'effectiveAt',
    'evidenceIds',
    'claimKey',
    'sourceAuthority',
  ], path, is);
  const category = oneOf(entry.category, PESTEL_CATEGORY_NAMES, `${path}.category`, is);
  const summary = str(entry.summary, `${path}.summary`, is);
  const jurisdiction = str(entry.jurisdiction, `${path}.jurisdiction`, is);
  const direction = oneOf(entry.direction, PESTEL_DIRECTION_NAMES, `${path}.direction`, is);
  const materiality = oneOf(entry.materiality, MATERIALITY_NAMES, `${path}.materiality`, is);
  const horizon = oneOf(entry.horizon, PESTEL_HORIZON_NAMES, `${path}.horizon`, is);
  const affectedAssumption = str(entry.affectedAssumption, `${path}.affectedAssumption`, is);
  const publishedAt = optStr(entry.publishedAt, `${path}.publishedAt`, is);
  const effectiveAt = optStr(entry.effectiveAt, `${path}.effectiveAt`, is);
  const evidenceIds = strArray(entry.evidenceIds, `${path}.evidenceIds`, is, 1);
  const claimKey = ident(entry.claimKey, `${path}.claimKey`, is);
  if (
    category === null || summary === null || jurisdiction === null || direction === null ||
    materiality === null || horizon === null || affectedAssumption === null ||
    publishedAt === null || effectiveAt === null || evidenceIds === null || claimKey === null
  ) return null;
  if (expectedCategory !== null && category !== expectedCategory) {
    is.add(`${path}.category`, 'item is filed under a different category');
  }
  return {
    category,
    summary,
    jurisdiction,
    direction,
    materiality,
    horizon,
    affectedAssumption,
    ...(publishedAt === undefined ? {} : { publishedAt }),
    ...(effectiveAt === undefined ? {} : { effectiveAt }),
    // Only the one value means anything; anything else is a report field this
    // build does not understand, and reading it as a flag would be a guess.
    ...(entry.sourceAuthority === 'unverified' ? { sourceAuthority: 'unverified' as const } : {}),
    evidenceIds,
    claimKey,
  };
}

function readPestelV11(value: unknown, is: Issues): PestelBodyV11 | null {
  const body = rec(value, 'report', is);
  if (!body) return null;
  strict(body, ['categories', 'pointsToConsider', 'insufficientReason'], 'report', is);

  const insufficientReason = nullableStr(body.insufficientReason, 'report.insufficientReason', is);
  const rawCategories = arr(body.categories, 'report.categories', is);

  const categories: PestelBodyV11['categories'] = [];
  for (let i = 0; i < (rawCategories ?? []).length; i++) {
    const path = `report.categories.${i}`;
    const entry = rec(rawCategories![i], path, is);
    if (!entry) continue;
    strict(entry, ['category', 'posture', 'items', 'claimKey'], path, is);
    const category = oneOf(entry.category, PESTEL_CATEGORY_NAMES, `${path}.category`, is);
    const posture = oneOf(entry.posture, PESTEL_POSTURE_NAMES, `${path}.posture`, is);
    const claimKey = ident(entry.claimKey, `${path}.claimKey`, is);
    const rawItems = arr(entry.items, `${path}.items`, is);
    const items: PestelItemV11[] = [];
    for (let j = 0; j < (rawItems ?? []).length; j++) {
      const item = readPestelItemV11(rawItems![j], `${path}.items.${j}`, is, category);
      if (item) items.push(item);
    }
    if (category === null || posture === null || claimKey === null) continue;
    if (claimKey !== `pestel.${category}`) {
      is.add(`${path}.claimKey`, 'claim key does not match its category');
    }
    // The posture is a deterministic function of the accepted items. A category
    // rated from nothing is the aggregate standing in for the evidence.
    if (posture === 'insufficient_evidence' && items.length > 0) {
      is.add(`${path}.posture`, 'a category with supported items is not insufficient');
    }
    if (posture !== 'insufficient_evidence' && items.length === 0) {
      is.add(`${path}.posture`, 'a category with no supported item cannot have a posture');
    }
    categories.push({ category, posture, items, claimKey });
  }

  const points: PestelBodyV11['pointsToConsider'] = [];
  for (const [i, raw] of (arr(body.pointsToConsider, 'report.pointsToConsider', is) ?? []).entries()) {
    const path = `report.pointsToConsider.${i}`;
    const entry = rec(raw, path, is);
    if (!entry) continue;
    strict(entry, ['kind', 'text', 'relatedCategories'], path, is);
    const kind = oneOf(entry.kind, POINT_KIND_NAMES, `${path}.kind`, is);
    const text = str(entry.text, `${path}.text`, is);
    const relatedCategories = enumArray(entry.relatedCategories, PESTEL_CATEGORY_NAMES, `${path}.relatedCategories`, is);
    if (kind === null || text === null || relatedCategories === null) continue;
    points.push({ kind, text, relatedCategories });
  }

  if (insufficientReason === undefined) return null;

  const supported = categories.filter((c) => c.posture !== 'insufficient_evidence').length;
  if (insufficientReason === null && supported === 0) {
    is.add('report.insufficientReason', 'no supported category must state why the report is insufficient');
  }
  if (new Set(categories.map((c) => c.category)).size !== categories.length) {
    is.add('report.categories', 'duplicate category');
  }
  assertUniqueKeys(
    [...categories.map((c) => c.claimKey), ...categories.flatMap((c) => c.items.map((i) => i.claimKey))],
    'report',
    is,
  );

  if (!is.ok) return null;
  return { categories, pointsToConsider: points, insufficientReason };
}

function readMarketFactor(value: unknown, path: string, is: Issues) {
  const entry = rec(value, path, is);
  if (!entry) return null;
  strict(entry, ['label', 'low', 'base', 'high', 'evidenceIds', 'founderProvided', 'claimKey'], path, is);
  const label = str(entry.label, `${path}.label`, is);
  const low = fin(entry.low, `${path}.low`, is);
  const base = fin(entry.base, `${path}.base`, is);
  const high = fin(entry.high, `${path}.high`, is);
  const evidenceIds = strArray(entry.evidenceIds, `${path}.evidenceIds`, is);
  const founderProvided = bool(entry.founderProvided, `${path}.founderProvided`, is);
  const claimKey = ident(entry.claimKey, `${path}.claimKey`, is);
  if (
    label === null || low === null || base === null || high === null ||
    evidenceIds === null || founderProvided === null || claimKey === null
  ) return null;
  // Attribution is EXPLICIT, never inferred from an empty citation list. A
  // researched factor whose evidence failed to persist would otherwise be
  // relabelled as something the founder said.
  if (!founderProvided && evidenceIds.length === 0) {
    return is.add(`${path}.evidenceIds`, 'a researched factor requires at least one evidence id');
  }
  if (!(low <= base && base <= high)) return is.add(path, 'bounds must be ordered low <= base <= high');
  return { label, low, base, high, evidenceIds, founderProvided, claimKey };
}

function readMarketV11(value: unknown, is: Issues): MarketValidationBodyV11 | null {
  const body = rec(value, 'report', is);
  if (!body) return null;
  strict(body, [
    'definitionCompatible',
    'definitionComparison',
    'definitionNotes',
    'topDown',
    'bottomUp',
    'founderEstimate',
    'corroboratedRange',
    'methodConflict',
    'conclusion',
    'conclusionExplanation',
    'comparisonClaimKey',
    'insufficientReason',
  ], 'report', is);

  const definitionCompatible = bool(body.definitionCompatible, 'report.definitionCompatible', is);
  const definitionNotes = strArray(body.definitionNotes, 'report.definitionNotes', is);
  const methodConflict = bool(body.methodConflict, 'report.methodConflict', is);
  const conclusion = oneOf(body.conclusion, MARKET_CONCLUSION_NAMES, 'report.conclusion', is);
  const conclusionExplanation = str(body.conclusionExplanation, 'report.conclusionExplanation', is);
  const comparisonClaimKey = optStr(body.comparisonClaimKey, 'report.comparisonClaimKey', is);
  const insufficientReason = nullableStr(body.insufficientReason, 'report.insufficientReason', is);
  const corroboratedRange = body.corroboratedRange === null
    ? null
    : range(body.corroboratedRange, 'report.corroboratedRange', is);

  const definitionComparison: MarketValidationBodyV11['definitionComparison'] = [];
  const COMPARISON_FIELDS = [
    'market_definition',
    'customer_unit',
    'geography',
    'currency',
    'base_year',
    'scope',
  ] as const;
  for (const [i, raw] of (arr(body.definitionComparison, 'report.definitionComparison', is) ?? []).entries()) {
    const path = `report.definitionComparison.${i}`;
    const entry = rec(raw, path, is);
    if (!entry) continue;
    strict(entry, ['field', 'founderValue', 'evidenceValue', 'compatible', 'note'], path, is);
    const field = oneOf(entry.field, COMPARISON_FIELDS, `${path}.field`, is);
    const founderValue = entry.founderValue === null ? null : str(entry.founderValue, `${path}.founderValue`, is);
    const evidenceValue = entry.evidenceValue === null ? null : str(entry.evidenceValue, `${path}.evidenceValue`, is);
    const compatible = bool(entry.compatible, `${path}.compatible`, is);
    const note = optStr(entry.note, `${path}.note`, is);
    if (field === null || compatible === null || note === null) continue;
    definitionComparison.push({
      field,
      founderValue: founderValue ?? null,
      evidenceValue: evidenceValue ?? null,
      compatible,
      ...(note === undefined ? {} : { note }),
    });
  }

  // ---- top down -----------------------------------------------------------
  const topDownRec = rec(body.topDown, 'report.topDown', is);
  let topDown: MarketValidationBodyV11['topDown'] | null = null;
  if (topDownRec) {
    strict(topDownRec, ['supported', 'base', 'insufficientReason', 'narrowingFactors', 'range', 'claimKey'], 'report.topDown', is);
    const supported = bool(topDownRec.supported, 'report.topDown.supported', is);
    const base = topDownRec.base === undefined
      ? undefined
      : materialNumber(topDownRec.base, 'report.topDown.base', is);
    const reason = optStr(topDownRec.insufficientReason, 'report.topDown.insufficientReason', is);
    const claimKey = optStr(topDownRec.claimKey, 'report.topDown.claimKey', is);
    const rangeValue = optRange(topDownRec.range, 'report.topDown.range', is);
    const factors: NonNullable<MarketValidationBodyV11['topDown']>['narrowingFactors'] = [];
    for (const [i, raw] of (arr(topDownRec.narrowingFactors, 'report.topDown.narrowingFactors', is) ?? []).entries()) {
      const factor = readMarketFactor(raw, `report.topDown.narrowingFactors.${i}`, is);
      if (factor) factors.push(factor);
    }
    if (
      supported !== null && base !== null && reason !== null && claimKey !== null && rangeValue !== null
    ) {
      if (supported && (base === undefined || rangeValue === undefined || claimKey === undefined)) {
        is.add('report.topDown', 'a supported top-down estimate needs a base, a range and a claim key');
      }
      if (!supported && reason === undefined) {
        is.add('report.topDown.insufficientReason', 'an unsupported top-down estimate must say why');
      }
      topDown = {
        supported,
        ...(base === undefined ? {} : { base }),
        ...(reason === undefined ? {} : { insufficientReason: reason }),
        narrowingFactors: factors,
        ...(rangeValue === undefined ? {} : { range: rangeValue }),
        ...(claimKey === undefined ? {} : { claimKey }),
      };
    }
  }

  // ---- bottom up ----------------------------------------------------------
  const bottomUpRec = rec(body.bottomUp, 'report.bottomUp', is);
  let bottomUp: MarketValidationBodyV11['bottomUp'] | null = null;
  if (bottomUpRec) {
    strict(bottomUpRec, ['supported', 'basis', 'tam', 'sam', 'som', 'inputs', 'insufficientReason', 'claimKey'], 'report.bottomUp', is);
    const supported = bool(bottomUpRec.supported, 'report.bottomUp.supported', is);
    const basis = oneOf(
      bottomUpRec.basis,
      ['independent', 'founder_stated', 'insufficient_evidence'] as const,
      'report.bottomUp.basis',
      is,
    );
    const tam = optRange(bottomUpRec.tam, 'report.bottomUp.tam', is);
    const sam = optRange(bottomUpRec.sam, 'report.bottomUp.sam', is);
    const som = optRange(bottomUpRec.som, 'report.bottomUp.som', is);
    const reason = optStr(bottomUpRec.insufficientReason, 'report.bottomUp.insufficientReason', is);
    const claimKey = optStr(bottomUpRec.claimKey, 'report.bottomUp.claimKey', is);
    const inputs: NonNullable<MarketValidationBodyV11['bottomUp']>['inputs'] = [];
    for (const [i, raw] of (arr(bottomUpRec.inputs, 'report.bottomUp.inputs', is) ?? []).entries()) {
      const factor = readMarketFactor(raw, `report.bottomUp.inputs.${i}`, is);
      if (factor) inputs.push(factor);
    }
    if (
      supported !== null && basis !== null && tam !== null && sam !== null && som !== null &&
      reason !== null && claimKey !== null
    ) {
      // THE RULE THIS WHOLE FIELD EXISTS FOR. "Independent" means every input
      // came from retained evidence. One founder-provided input and it is not
      // independent, whatever the label says.
      if (basis === 'independent' && inputs.some((i) => i.founderProvided)) {
        is.add('report.bottomUp.basis', 'an independent estimate cannot consume a founder-provided input');
      }
      if (basis === 'independent' && inputs.length === 0) {
        is.add('report.bottomUp.inputs', 'an independent estimate must show the inputs it was built from');
      }
      if (supported && basis === 'insufficient_evidence') {
        is.add('report.bottomUp.basis', 'a supported estimate has a basis');
      }
      if (!supported && basis !== 'insufficient_evidence') {
        is.add('report.bottomUp.basis', 'an unsupported estimate has no basis');
      }
      if (supported && sam === undefined && som === undefined && tam === undefined) {
        is.add('report.bottomUp', 'a supported estimate must publish at least one figure');
      }
      if (!supported && reason === undefined) {
        is.add('report.bottomUp.insufficientReason', 'an unsupported bottom-up estimate must say why');
      }
      bottomUp = {
        supported,
        basis,
        ...(tam === undefined ? {} : { tam }),
        ...(sam === undefined ? {} : { sam }),
        ...(som === undefined ? {} : { som }),
        inputs,
        ...(reason === undefined ? {} : { insufficientReason: reason }),
        ...(claimKey === undefined ? {} : { claimKey }),
      };
    }
  }

  let founderEstimate: MarketValidationBodyV11['founderEstimate'] = null;
  if (body.founderEstimate !== null && body.founderEstimate !== undefined) {
    const entry = rec(body.founderEstimate, 'report.founderEstimate', is);
    if (entry) {
      strict(entry, ['tam', 'sam', 'som'], 'report.founderEstimate', is);
      const tam = optRange(entry.tam, 'report.founderEstimate.tam', is);
      const sam = optRange(entry.sam, 'report.founderEstimate.sam', is);
      const som = optRange(entry.som, 'report.founderEstimate.som', is);
      if (tam !== null && sam !== null && som !== null) {
        founderEstimate = {
          ...(tam === undefined ? {} : { tam }),
          ...(sam === undefined ? {} : { sam }),
          ...(som === undefined ? {} : { som }),
        };
      }
    }
  }

  if (
    definitionCompatible === null || definitionNotes === null || methodConflict === null ||
    conclusion === null || conclusionExplanation === null || comparisonClaimKey === null ||
    insufficientReason === undefined || corroboratedRange === undefined ||
    topDown === null || bottomUp === null
  ) return null;

  if (!topDown.supported && !bottomUp.supported && insufficientReason === null) {
    is.add('report.insufficientReason', 'neither method supported must state why the report is insufficient');
  }
  if (corroboratedRange !== null && comparisonClaimKey === undefined) {
    is.add('report.comparisonClaimKey', 'a corroborated range is a published conclusion and needs a claim key');
  }
  if (!definitionCompatible && definitionComparison.every((c) => c.compatible)) {
    is.add('report.definitionCompatible', 'incompatibility must name the field that differs');
  }
  assertUniqueKeys(
    [
      ...(topDown.claimKey ? [topDown.claimKey] : []),
      ...(bottomUp.claimKey ? [bottomUp.claimKey] : []),
      ...(comparisonClaimKey ? [comparisonClaimKey] : []),
      ...topDown.narrowingFactors.map((f) => f.claimKey),
      ...bottomUp.inputs.map((f) => f.claimKey),
    ],
    'report',
    is,
  );

  if (!is.ok) return null;
  return {
    definitionCompatible,
    definitionComparison,
    definitionNotes,
    topDown,
    bottomUp,
    founderEstimate,
    corroboratedRange,
    methodConflict,
    conclusion,
    conclusionExplanation,
    ...(comparisonClaimKey === undefined ? {} : { comparisonClaimKey }),
    insufficientReason,
  };
}

function readExitV11(value: unknown, is: Issues): ExitStrategyBodyV11 | null {
  const body = rec(value, 'report', is);
  if (!body) return null;
  strict(body, [
    'pathways',
    'mostSupportedPathways',
    'candidates',
    'comparables',
    'readinessGaps',
    'milestones',
    'thesisComparison',
    'assessedThesisVersionId',
    'insufficientReason',
  ], 'report', is);

  const thesisComparison = oneOf(body.thesisComparison, THESIS_COMPARISON_NAMES, 'report.thesisComparison', is);
  const assessedThesisVersionId = ident(body.assessedThesisVersionId, 'report.assessedThesisVersionId', is);
  const mostSupportedPathways = enumArray(body.mostSupportedPathways, EXIT_PATHWAY_NAMES, 'report.mostSupportedPathways', is);
  const insufficientReason = nullableStr(body.insufficientReason, 'report.insufficientReason', is);

  const pathways: ExitStrategyBodyV11['pathways'] = [];
  for (const [i, raw] of (arr(body.pathways, 'report.pathways', is) ?? []).entries()) {
    const path = `report.pathways.${i}`;
    const entry = rec(raw, path, is);
    if (!entry) continue;
    strict(entry, ['pathway', 'tier', 'drivers', 'evidenceIds', 'claimKey'], path, is);
    const pathway = oneOf(entry.pathway, EXIT_PATHWAY_NAMES, `${path}.pathway`, is);
    const tier = oneOf(entry.tier, EXIT_PATHWAY_TIER_NAMES, `${path}.tier`, is);
    const evidenceIds = strArray(entry.evidenceIds, `${path}.evidenceIds`, is);
    const claimKey = ident(entry.claimKey, `${path}.claimKey`, is);
    const drivers: ExitStrategyBodyV11['pathways'][number]['drivers'] = [];
    for (const [j, rawDriver] of (arr(entry.drivers, `${path}.drivers`, is) ?? []).entries()) {
      const dPath = `${path}.drivers.${j}`;
      const driverEntry = rec(rawDriver, dPath, is);
      if (!driverEntry) continue;
      strict(driverEntry, ['driver', 'judgement', 'rationale', 'evidenceIds', 'claimKey', 'ground'], dPath, is);
      const driver = ident(driverEntry.driver, `${dPath}.driver`, is);
      const judgement = oneOf(driverEntry.judgement, PATHWAY_DRIVER_JUDGEMENT_NAMES, `${dPath}.judgement`, is);
      const rationale = str(driverEntry.rationale, `${dPath}.rationale`, is);
      const driverEvidence = strArray(driverEntry.evidenceIds, `${dPath}.evidenceIds`, is);
      const driverKey = ident(driverEntry.claimKey, `${dPath}.claimKey`, is);
      const ground = oneOf(driverEntry.ground, STATEMENT_GROUNDS, `${dPath}.ground`, is);
      if (
        driver === null || judgement === null || rationale === null ||
        driverEvidence === null || driverKey === null || ground === null
      ) continue;
      if (ground === 'evidence_backed' && driverEvidence.length === 0) {
        is.add(`${dPath}.evidenceIds`, 'an evidence_backed driver requires at least one evidence id');
      }
      drivers.push({ driver, judgement, rationale, evidenceIds: driverEvidence, claimKey: driverKey, ground });
    }
    if (pathway === null || tier === null || evidenceIds === null || claimKey === null) continue;
    if (claimKey !== `exit.pathway.${pathway}`) {
      is.add(`${path}.claimKey`, 'claim key does not match its pathway');
    }
    pathways.push({ pathway, tier, drivers, evidenceIds, claimKey });
  }

  const candidates: AcquirerCandidateV11[] = [];
  for (const [i, raw] of (arr(body.candidates, 'report.candidates', is) ?? []).entries()) {
    const path = `report.candidates.${i}`;
    const entry = rec(raw, path, is);
    if (!entry) continue;
    strict(entry, [
      'name',
      'entityId',
      'fitTier',
      'dimensions',
      'evidenceIds',
      'counterargument',
      'evidenceGaps',
      'claimKey',
      'identity',
    ], path, is);
    const name = ident(entry.name, `${path}.name`, is);
    const entityId = ident(entry.entityId, `${path}.entityId`, is);
    const fitTier = oneOf(entry.fitTier, ACQUIRER_FIT_TIER_NAMES, `${path}.fitTier`, is);
    const evidenceIds = strArray(entry.evidenceIds, `${path}.evidenceIds`, is, 1);
    const counterargument = groundedStatement(entry.counterargument, `${path}.counterargument`, is);
    const evidenceGaps = strArray(entry.evidenceGaps, `${path}.evidenceGaps`, is);
    const claimKey = ident(entry.claimKey, `${path}.claimKey`, is);
    const identity = identityBlock(entry.identity, `${path}.identity`, is);
    const dimensions: AcquirerCandidateV11['dimensions'] = [];
    for (const [j, rawDim] of (arr(entry.dimensions, `${path}.dimensions`, is) ?? []).entries()) {
      const dPath = `${path}.dimensions.${j}`;
      const dim = rec(rawDim, dPath, is);
      if (!dim) continue;
      strict(dim, ['dimension', 'supported', 'rationale', 'evidenceIds', 'ground'], dPath, is);
      const dimension = oneOf(dim.dimension, ACQUIRER_FIT_DIMENSION_NAMES, `${dPath}.dimension`, is);
      const supported = bool(dim.supported, `${dPath}.supported`, is);
      const rationale = str(dim.rationale, `${dPath}.rationale`, is);
      const dimEvidence = strArray(dim.evidenceIds, `${dPath}.evidenceIds`, is);
      const ground = oneOf(dim.ground, STATEMENT_GROUNDS, `${dPath}.ground`, is);
      if (dimension === null || supported === null || rationale === null || dimEvidence === null || ground === null) {
        continue;
      }
      // A dimension counted as SUPPORTED with no evidence is the fit tier
      // supporting itself.
      if (supported && dimEvidence.length === 0) {
        is.add(`${dPath}.evidenceIds`, 'a supported dimension requires at least one evidence id');
      }
      dimensions.push({ dimension, supported, rationale, evidenceIds: dimEvidence, ground });
    }
    if (
      name === null || entityId === null || fitTier === null || evidenceIds === null ||
      counterargument === null || evidenceGaps === null || claimKey === null || identity === null
    ) continue;
    if (claimKey !== `exit.candidate.${entityId}`) {
      is.add(`${path}.claimKey`, 'claim key does not match its entity id');
    }
    // A named acquirer is where being wrong stops being abstract. An identity
    // nobody could establish must not reach a shortlist.
    if (identity.matchMethod === 'unresolved') {
      is.add(`${path}.identity.matchMethod`, 'a named candidate requires a resolved identity');
    }
    candidates.push({
      name,
      entityId,
      fitTier,
      dimensions,
      evidenceIds,
      counterargument,
      evidenceGaps,
      claimKey,
      identity,
    });
  }

  const comparables: ComparableTransactionV11[] = [];
  for (const [i, raw] of (arr(body.comparables, 'report.comparables', is) ?? []).entries()) {
    const path = `report.comparables.${i}`;
    const entry = rec(raw, path, is);
    if (!entry) continue;
    strict(entry, [
      'buyer',
      'target',
      'announcedAt',
      'rationale',
      'disclosedValue',
      'disclosedCurrency',
      'valueDisclosed',
      'evidenceIds',
      'claimKey',
    ], path, is);
    const buyer = str(entry.buyer, `${path}.buyer`, is);
    const target = str(entry.target, `${path}.target`, is);
    const announcedAt = str(entry.announcedAt, `${path}.announcedAt`, is);
    const rationale = optStr(entry.rationale, `${path}.rationale`, is);
    const disclosedValue = entry.disclosedValue === undefined
      ? undefined
      : fin(entry.disclosedValue, `${path}.disclosedValue`, is);
    const disclosedCurrency = optStr(entry.disclosedCurrency, `${path}.disclosedCurrency`, is);
    const valueDisclosed = bool(entry.valueDisclosed, `${path}.valueDisclosed`, is);
    const evidenceIds = strArray(entry.evidenceIds, `${path}.evidenceIds`, is, 1);
    const claimKey = ident(entry.claimKey, `${path}.claimKey`, is);
    if (
      buyer === null || target === null || announcedAt === null || rationale === null ||
      disclosedValue === null || disclosedCurrency === null || valueDisclosed === null ||
      evidenceIds === null || claimKey === null
    ) continue;
    if (!valueDisclosed && (disclosedValue !== undefined || disclosedCurrency !== undefined)) {
      is.add(`${path}.disclosedValue`, 'a value may only be present when the terms were disclosed');
    }
    comparables.push({
      buyer,
      target,
      announcedAt,
      ...(rationale === undefined ? {} : { rationale }),
      ...(disclosedValue === undefined ? {} : { disclosedValue }),
      ...(disclosedCurrency === undefined ? {} : { disclosedCurrency }),
      valueDisclosed,
      evidenceIds,
      claimKey,
    });
  }

  const readinessGaps: GroundedStatement[] = [];
  for (const [i, raw] of (arr(body.readinessGaps, 'report.readinessGaps', is) ?? []).entries()) {
    const gap = groundedStatement(raw, `report.readinessGaps.${i}`, is);
    if (gap) readinessGaps.push(gap);
  }
  const milestones: GroundedStatement[] = [];
  for (const [i, raw] of (arr(body.milestones, 'report.milestones', is) ?? []).entries()) {
    const milestone = groundedStatement(raw, `report.milestones.${i}`, is);
    if (milestone) milestones.push(milestone);
  }

  if (
    thesisComparison === null || assessedThesisVersionId === null ||
    mostSupportedPathways === null || insufficientReason === undefined
  ) return null;

  const tiered = pathways.filter((p) => p.tier !== 'insufficient_evidence');
  if (insufficientReason === null && tiered.length === 0) {
    is.add('report.insufficientReason', 'no tiered pathway must state why the report is insufficient');
  }
  const tieredNames = new Set(tiered.map((p) => p.pathway));
  for (const pathway of mostSupportedPathways) {
    if (!tieredNames.has(pathway)) {
      is.add('report.mostSupportedPathways', 'names a pathway that was not tiered');
    }
  }
  if (new Set(candidates.map((c) => c.entityId)).size !== candidates.length) {
    is.add('report.candidates', 'duplicate entityId');
  }
  assertUniqueKeys(
    [
      ...pathways.map((p) => p.claimKey),
      ...pathways.flatMap((p) => p.drivers.map((d) => d.claimKey)),
      ...candidates.map((c) => c.claimKey),
      ...comparables.map((c) => c.claimKey),
    ],
    'report',
    is,
  );

  if (!is.ok) return null;
  return {
    pathways,
    mostSupportedPathways,
    candidates,
    comparables,
    readinessGaps,
    milestones,
    thesisComparison,
    assessedThesisVersionId,
    insufficientReason,
  };
}

// ===========================================================================
// 1.0.0 readers — frozen
// ===========================================================================

/** Fields each historical writer emitted. Strict per variant — never a union of keys. */
const COMPETITION_V10_KEYS: Record<CompetitionV10Variant, readonly string[]> = {
  A: ['outputMode', 'axes', 'self', 'entities', 'fallbackReason', 'insufficientReason'],
  B: ['outputMode', 'axes', 'self', 'entities', 'notReFound', 'fallbackReason', 'insufficientReason'],
  C: [
    'outputMode',
    'axes',
    'self',
    'entities',
    'notReFound',
    'notReassessed',
    'comparisonStatus',
    'fallbackReason',
    'insufficientReason',
  ],
};

/** Whether an entity carried `discovery`, per writer. B emitted it always. */
const COMPETITION_V10_DISCOVERY: Record<CompetitionV10Variant, 'absent' | 'required' | 'optional'> = {
  A: 'absent',
  B: 'required',
  C: 'optional',
};

function readCompetitionV10Variant(
  variant: CompetitionV10Variant,
  value: unknown,
  is: Issues,
): CompetitionBodyV10 | null {
  const body = rec(value, 'report', is);
  if (!body) return null;
  strict(body, COMPETITION_V10_KEYS[variant], 'report', is);
  const outputMode = oneOf(body.outputMode, COMPETITION_OUTPUT_MODE_NAMES, 'report.outputMode', is);
  const fallbackReason = nullableStr(body.fallbackReason, 'report.fallbackReason', is);
  const insufficientReason = nullableStr(body.insufficientReason, 'report.insufficientReason', is);

  const axes: CompetitionBodyV10['axes'] = [];
  for (const [i, raw] of (arr(body.axes, 'report.axes', is) ?? []).entries()) {
    const path = `report.axes.${i}`;
    const entry = rec(raw, path, is);
    if (!entry) continue;
    strict(entry, ['axis', 'lowLabel', 'highLabel', 'relevanceRationale'], path, is);
    const axis = oneOf(entry.axis, COMPETITION_AXIS_NAMES, `${path}.axis`, is);
    const lowLabel = str(entry.lowLabel, `${path}.lowLabel`, is);
    const highLabel = str(entry.highLabel, `${path}.highLabel`, is);
    const relevanceRationale = optStr(entry.relevanceRationale, `${path}.relevanceRationale`, is);
    if (axis === null || lowLabel === null || highLabel === null || relevanceRationale === null) continue;
    axes.push({ axis, lowLabel, highLabel, ...(relevanceRationale === undefined ? {} : { relevanceRationale }) });
  }
  if (axes.length > 2) is.add('report.axes', 'at most two axes');

  const discoveryRule = COMPETITION_V10_DISCOVERY[variant];
  const entities: CompetitionBodyV10['entities'] = [];
  for (const [i, raw] of (arr(body.entities, 'report.entities', is) ?? []).entries()) {
    const path = `report.entities.${i}`;
    const entry = rec(raw, path, is);
    if (!entry) continue;
    strict(
      entry,
      discoveryRule === 'absent'
        ? ['name', 'entityId', 'classification', 'positions', 'evidenceIds']
        : ['name', 'entityId', 'classification', 'positions', 'evidenceIds', 'discovery'],
      path,
      is,
    );
    const name = ident(entry.name, `${path}.name`, is);
    const entityId = ident(entry.entityId, `${path}.entityId`, is);
    const classification = oneOf(entry.classification, COMPETITOR_CLASS_NAMES, `${path}.classification`, is);
    const pos = positions(entry.positions, `${path}.positions`, is);
    const evidenceIds = strArray(entry.evidenceIds, `${path}.evidenceIds`, is);
    if (discoveryRule === 'required' && entry.discovery === undefined) {
      is.add(`${path}.discovery`, 'this writer always emitted discovery');
      continue;
    }
    const discovery = entry.discovery === undefined
      ? undefined
      : oneOf(entry.discovery, ['new', 'reconfirmed'] as const, `${path}.discovery`, is);
    if (
      name === null || entityId === null || classification === null || pos === null ||
      evidenceIds === null || discovery === null
    ) continue;
    entities.push({
      name,
      entityId,
      classification,
      positions: pos,
      evidenceIds,
      ...(discovery === undefined ? {} : { discovery }),
    });
  }

  let self: CompetitionBodyV10['self'] = null;
  if (body.self !== null && body.self !== undefined) {
    const entry = rec(body.self, 'report.self', is);
    if (entry) {
      // `self` never carried an entityId at 1.0.0 — `CompetitionValidationResult`
      // returned `{ positions } | null` for the whole window.
      strict(entry, ['positions'], 'report.self', is);
      const pos = positions(entry.positions, 'report.self.positions', is);
      if (pos !== null) self = { positions: pos };
    }
  }

  let notReFound: CompetitionBodyV10['notReFound'];
  if (variant !== 'A') {
    notReFound = [];
    for (const [i, raw] of (arr(body.notReFound, 'report.notReFound', is) ?? []).entries()) {
      const path = `report.notReFound.${i}`;
      const entry = rec(raw, path, is);
      if (!entry) continue;
      strict(entry, ['entityId', 'name'], path, is);
      const entityId = ident(entry.entityId, `${path}.entityId`, is);
      const name = ident(entry.name, `${path}.name`, is);
      if (entityId !== null && name !== null) notReFound.push({ entityId, name });
    }
  }

  let notReassessed: CompetitionBodyV10['notReassessed'];
  let comparisonStatus: CompetitionBodyV10['comparisonStatus'];
  if (variant === 'C') {
    notReassessed = [];
    for (const [i, raw] of (arr(body.notReassessed, 'report.notReassessed', is) ?? []).entries()) {
      const path = `report.notReassessed.${i}`;
      const entry = rec(raw, path, is);
      if (!entry) continue;
      strict(entry, ['entityId', 'name', 'reason'], path, is);
      const entityId = ident(entry.entityId, `${path}.entityId`, is);
      const name = ident(entry.name, `${path}.name`, is);
      const reason = oneOf(entry.reason, NOT_REASSESSED_REASONS, `${path}.reason`, is);
      if (entityId !== null && name !== null && reason !== null) notReassessed.push({ entityId, name, reason });
    }
    const status = oneOf(body.comparisonStatus, COMPARISON_STATUS_NAMES, 'report.comparisonStatus', is);
    if (status === null) return null;
    comparisonStatus = status;
  }

  if (outputMode === null || fallbackReason === undefined || insufficientReason === undefined) return null;
  if (!is.ok) return null;
  return {
    variant,
    outputMode,
    axes,
    self,
    entities,
    ...(notReFound === undefined ? {} : { notReFound }),
    ...(notReassessed === undefined ? {} : { notReassessed }),
    ...(comparisonStatus === undefined ? {} : { comparisonStatus }),
    fallbackReason,
    insufficientReason,
  };
}

/**
 * Try each historical writer, NEWEST FIRST.
 *
 * Newest first because the later shapes carry strictly more keys, and a strict
 * reader for an older variant rejects them outright — so the first success is
 * unambiguous rather than a coincidence of overlapping optional fields. A row
 * that satisfies none of the three is malformed, and the issues reported are the
 * NEWEST variant's, which is the one nearly every stored row will be.
 */
function readCompetitionV10(value: unknown, is: Issues): CompetitionBodyV10 | null {
  const order: CompetitionV10Variant[] = ['C', 'B', 'A'];
  let firstIssues: Issues | null = null;
  for (const variant of order) {
    const attempt = new Issues();
    const parsed = readCompetitionV10Variant(variant, value, attempt);
    if (parsed !== null && attempt.ok) return parsed;
    if (firstIssues === null) firstIssues = attempt;
  }
  for (const issue of firstIssues?.list ?? ['report: matched no 1.0.0 competition writer']) {
    is.list.push(issue);
  }
  return null;
}

function readPorterV10(value: unknown, is: Issues): PorterBodyV10 | null {
  const body = rec(value, 'report', is);
  if (!body) return null;
  strict(body, ['forces', 'overallSynthesis', 'insufficientReason'], 'report', is);
  const overallSynthesis = str(body.overallSynthesis, 'report.overallSynthesis', is);
  const insufficientReason = nullableStr(body.insufficientReason, 'report.insufficientReason', is);

  const forces: PorterBodyV10['forces'] = [];
  for (const [i, raw] of (arr(body.forces, 'report.forces', is) ?? []).entries()) {
    const path = `report.forces.${i}`;
    const entry = rec(raw, path, is);
    if (!entry) continue;
    strict(entry, ['force', 'rating', 'drivers', 'supportedDriverCount', 'unknownDrivers'], path, is);
    const force = oneOf(entry.force, PORTER_FORCE_NAMES, `${path}.force`, is);
    const rating = oneOf(entry.rating, PORTER_RATING_NAMES, `${path}.rating`, is);
    const supportedDriverCount = int(entry.supportedDriverCount, `${path}.supportedDriverCount`, is, 0, 64);
    const unknownDrivers = strArray(entry.unknownDrivers, `${path}.unknownDrivers`, is);
    const drivers: PorterBodyV10['forces'][number]['drivers'] = [];
    for (const [j, rawDriver] of (arr(entry.drivers, `${path}.drivers`, is) ?? []).entries()) {
      const dPath = `${path}.drivers.${j}`;
      const driverEntry = rec(rawDriver, dPath, is);
      if (!driverEntry) continue;
      strict(driverEntry, [
        'driver',
        'pressure',
        'observation',
        'interpretation',
        'evidenceIds',
        'counterEvidenceStatus',
      ], dPath, is);
      const driver = ident(driverEntry.driver, `${dPath}.driver`, is);
      const pressure = int(driverEntry.pressure, `${dPath}.pressure`, is, 0, 2);
      const observation = str(driverEntry.observation, `${dPath}.observation`, is);
      const interpretation = str(driverEntry.interpretation, `${dPath}.interpretation`, is);
      const evidenceIds = strArray(driverEntry.evidenceIds, `${dPath}.evidenceIds`, is, 1);
      const counterEvidenceStatus = oneOf(
        driverEntry.counterEvidenceStatus,
        COUNTER_EVIDENCE_STATUS_NAMES,
        `${dPath}.counterEvidenceStatus`,
        is,
      );
      if (
        driver === null || pressure === null || observation === null || interpretation === null ||
        evidenceIds === null || counterEvidenceStatus === null
      ) continue;
      drivers.push({
        driver,
        pressure: pressure as 0 | 1 | 2,
        observation,
        interpretation,
        evidenceIds,
        counterEvidenceStatus,
      });
    }
    if (force === null || rating === null || supportedDriverCount === null || unknownDrivers === null) continue;
    forces.push({ force, rating, drivers, supportedDriverCount, unknownDrivers });
  }

  if (overallSynthesis === null || insufficientReason === undefined) return null;
  if (!is.ok) return null;
  return { forces, overallSynthesis, insufficientReason };
}

function readPestelV10(value: unknown, is: Issues): PestelBodyV10 | null {
  const body = rec(value, 'report', is);
  if (!body) return null;
  strict(body, ['categories', 'pointsToConsider', 'insufficientReason'], 'report', is);
  const insufficientReason = nullableStr(body.insufficientReason, 'report.insufficientReason', is);

  const categories: PestelBodyV10['categories'] = [];
  for (const [i, raw] of (arr(body.categories, 'report.categories', is) ?? []).entries()) {
    const path = `report.categories.${i}`;
    const entry = rec(raw, path, is);
    if (!entry) continue;
    strict(entry, ['category', 'posture', 'items'], path, is);
    const category = oneOf(entry.category, PESTEL_CATEGORY_NAMES, `${path}.category`, is);
    const posture = oneOf(entry.posture, PESTEL_POSTURE_NAMES, `${path}.posture`, is);
    const items: PestelBodyV10['categories'][number]['items'] = [];
    for (const [j, rawItem] of (arr(entry.items, `${path}.items`, is) ?? []).entries()) {
      const iPath = `${path}.items.${j}`;
      const item = rec(rawItem, iPath, is);
      if (!item) continue;
      strict(item, [
        'category',
        'summary',
        'jurisdiction',
        'direction',
        'materiality',
        'horizon',
        'affectedAssumption',
        'publishedAt',
        'effectiveAt',
        'evidenceIds',
      ], iPath, is);
      const itemCategory = oneOf(item.category, PESTEL_CATEGORY_NAMES, `${iPath}.category`, is);
      const summary = str(item.summary, `${iPath}.summary`, is);
      const jurisdiction = str(item.jurisdiction, `${iPath}.jurisdiction`, is);
      const direction = oneOf(item.direction, PESTEL_DIRECTION_NAMES, `${iPath}.direction`, is);
      const materiality = oneOf(item.materiality, MATERIALITY_NAMES, `${iPath}.materiality`, is);
      const horizon = oneOf(item.horizon, PESTEL_HORIZON_NAMES, `${iPath}.horizon`, is);
      const affectedAssumption = str(item.affectedAssumption, `${iPath}.affectedAssumption`, is);
      const publishedAt = optStr(item.publishedAt, `${iPath}.publishedAt`, is);
      const effectiveAt = optStr(item.effectiveAt, `${iPath}.effectiveAt`, is);
      const evidenceIds = strArray(item.evidenceIds, `${iPath}.evidenceIds`, is, 1);
      if (
        itemCategory === null || summary === null || jurisdiction === null || direction === null ||
        materiality === null || horizon === null || affectedAssumption === null ||
        publishedAt === null || effectiveAt === null || evidenceIds === null
      ) continue;
      items.push({
        category: itemCategory,
        summary,
        jurisdiction,
        direction,
        materiality,
        horizon,
        affectedAssumption,
        ...(publishedAt === undefined ? {} : { publishedAt }),
        ...(effectiveAt === undefined ? {} : { effectiveAt }),
        evidenceIds,
      });
    }
    if (category === null || posture === null) continue;
    categories.push({ category, posture, items });
  }

  const points: PestelBodyV10['pointsToConsider'] = [];
  for (const [i, raw] of (arr(body.pointsToConsider, 'report.pointsToConsider', is) ?? []).entries()) {
    const path = `report.pointsToConsider.${i}`;
    const entry = rec(raw, path, is);
    if (!entry) continue;
    strict(entry, ['kind', 'text', 'relatedCategories'], path, is);
    const kind = oneOf(entry.kind, POINT_KIND_NAMES, `${path}.kind`, is);
    const text = str(entry.text, `${path}.text`, is);
    const relatedCategories = enumArray(entry.relatedCategories, PESTEL_CATEGORY_NAMES, `${path}.relatedCategories`, is);
    if (kind === null || text === null || relatedCategories === null) continue;
    points.push({ kind, text, relatedCategories });
  }

  if (insufficientReason === undefined) return null;
  if (!is.ok) return null;
  return { categories, pointsToConsider: points, insufficientReason };
}

function readMarketV10(value: unknown, is: Issues): MarketValidationBodyV10 | null {
  const body = rec(value, 'report', is);
  if (!body) return null;
  strict(body, [
    'definitionCompatible',
    'definitionNotes',
    'topDown',
    'bottomUp',
    'corroboratedRange',
    'methodConflict',
    'conclusion',
    'conclusionExplanation',
    'insufficientReason',
  ], 'report', is);

  const definitionCompatible = bool(body.definitionCompatible, 'report.definitionCompatible', is);
  const definitionNotes = strArray(body.definitionNotes, 'report.definitionNotes', is);
  const methodConflict = bool(body.methodConflict, 'report.methodConflict', is);
  const conclusion = oneOf(body.conclusion, MARKET_CONCLUSION_NAMES, 'report.conclusion', is);
  const conclusionExplanation = str(body.conclusionExplanation, 'report.conclusionExplanation', is);
  const insufficientReason = nullableStr(body.insufficientReason, 'report.insufficientReason', is);
  const corroboratedRange = body.corroboratedRange === null
    ? null
    : range(body.corroboratedRange, 'report.corroboratedRange', is);

  const topDownRec = rec(body.topDown, 'report.topDown', is);
  let topDown: MarketValidationBodyV10['topDown'] | null = null;
  if (topDownRec) {
    strict(topDownRec, ['supported', 'base', 'narrowingFactors', 'range'], 'report.topDown', is);
    const supported = bool(topDownRec.supported, 'report.topDown.supported', is);
    const base = topDownRec.base === undefined
      ? undefined
      : legacyMaterialNumber(topDownRec.base, 'report.topDown.base', is);
    const rangeValue = optRange(topDownRec.range, 'report.topDown.range', is);
    const factors: MarketValidationBodyV10['topDown']['narrowingFactors'] = [];
    for (const [i, raw] of (arr(topDownRec.narrowingFactors, 'report.topDown.narrowingFactors', is) ?? []).entries()) {
      const path = `report.topDown.narrowingFactors.${i}`;
      const entry = rec(raw, path, is);
      if (!entry) continue;
      strict(entry, ['label', 'low', 'base', 'high', 'evidenceIds', 'founderProvided'], path, is);
      const label = str(entry.label, `${path}.label`, is);
      const low = fin(entry.low, `${path}.low`, is);
      const factorBase = fin(entry.base, `${path}.base`, is);
      const high = fin(entry.high, `${path}.high`, is);
      const evidenceIds = strArray(entry.evidenceIds, `${path}.evidenceIds`, is);
      const founderProvided = bool(entry.founderProvided, `${path}.founderProvided`, is);
      if (
        label === null || low === null || factorBase === null || high === null ||
        evidenceIds === null || founderProvided === null
      ) continue;
      factors.push({ label, low, base: factorBase, high, evidenceIds, founderProvided });
    }
    if (supported !== null && base !== null && rangeValue !== null) {
      topDown = {
        supported,
        ...(base === undefined ? {} : { base }),
        narrowingFactors: factors,
        ...(rangeValue === undefined ? {} : { range: rangeValue }),
      };
    }
  }

  const bottomUpRec = rec(body.bottomUp, 'report.bottomUp', is);
  let bottomUp: MarketValidationBodyV10['bottomUp'] | null = null;
  if (bottomUpRec) {
    strict(bottomUpRec, ['supported', 'tam', 'sam', 'som'], 'report.bottomUp', is);
    const supported = bool(bottomUpRec.supported, 'report.bottomUp.supported', is);
    const tam = optRange(bottomUpRec.tam, 'report.bottomUp.tam', is);
    const sam = optRange(bottomUpRec.sam, 'report.bottomUp.sam', is);
    const som = optRange(bottomUpRec.som, 'report.bottomUp.som', is);
    if (supported !== null && tam !== null && sam !== null && som !== null) {
      bottomUp = {
        supported,
        ...(tam === undefined ? {} : { tam }),
        ...(sam === undefined ? {} : { sam }),
        ...(som === undefined ? {} : { som }),
      };
    }
  }

  if (
    definitionCompatible === null || definitionNotes === null || methodConflict === null ||
    conclusion === null || conclusionExplanation === null || insufficientReason === undefined ||
    corroboratedRange === undefined || topDown === null || bottomUp === null
  ) return null;
  if (!is.ok) return null;
  return {
    definitionCompatible,
    definitionNotes,
    topDown,
    bottomUp,
    corroboratedRange,
    methodConflict,
    conclusion,
    conclusionExplanation,
    insufficientReason,
  };
}

function readExitV10(value: unknown, is: Issues): ExitStrategyBodyV10 | null {
  const body = rec(value, 'report', is);
  if (!body) return null;
  strict(body, [
    'pathways',
    'mostSupportedPathways',
    'candidates',
    'comparables',
    'readinessGaps',
    'milestones',
    'thesisComparison',
    'assessedThesisVersionId',
    'insufficientReason',
  ], 'report', is);

  const thesisComparison = oneOf(body.thesisComparison, THESIS_COMPARISON_NAMES, 'report.thesisComparison', is);
  const assessedThesisVersionId = ident(body.assessedThesisVersionId, 'report.assessedThesisVersionId', is);
  const mostSupportedPathways = enumArray(body.mostSupportedPathways, EXIT_PATHWAY_NAMES, 'report.mostSupportedPathways', is);
  const readinessGaps = strArray(body.readinessGaps, 'report.readinessGaps', is);
  const milestones = strArray(body.milestones, 'report.milestones', is);
  const insufficientReason = nullableStr(body.insufficientReason, 'report.insufficientReason', is);

  const pathways: ExitStrategyBodyV10['pathways'] = [];
  for (const [i, raw] of (arr(body.pathways, 'report.pathways', is) ?? []).entries()) {
    const path = `report.pathways.${i}`;
    const entry = rec(raw, path, is);
    if (!entry) continue;
    strict(entry, ['pathway', 'tier', 'drivers', 'evidenceIds'], path, is);
    const pathway = oneOf(entry.pathway, EXIT_PATHWAY_NAMES, `${path}.pathway`, is);
    const tier = oneOf(entry.tier, EXIT_PATHWAY_TIER_NAMES, `${path}.tier`, is);
    const evidenceIds = strArray(entry.evidenceIds, `${path}.evidenceIds`, is);
    const drivers: ExitStrategyBodyV10['pathways'][number]['drivers'] = [];
    for (const [j, rawDriver] of (arr(entry.drivers, `${path}.drivers`, is) ?? []).entries()) {
      const dPath = `${path}.drivers.${j}`;
      const driverEntry = rec(rawDriver, dPath, is);
      if (!driverEntry) continue;
      strict(driverEntry, ['driver', 'judgement', 'rationale', 'evidenceIds'], dPath, is);
      const driver = ident(driverEntry.driver, `${dPath}.driver`, is);
      const judgement = oneOf(driverEntry.judgement, PATHWAY_DRIVER_JUDGEMENT_NAMES, `${dPath}.judgement`, is);
      const rationale = str(driverEntry.rationale, `${dPath}.rationale`, is);
      const driverEvidence = strArray(driverEntry.evidenceIds, `${dPath}.evidenceIds`, is);
      if (driver === null || judgement === null || rationale === null || driverEvidence === null) continue;
      drivers.push({ driver, judgement, rationale, evidenceIds: driverEvidence });
    }
    if (pathway === null || tier === null || evidenceIds === null) continue;
    pathways.push({ pathway, tier, drivers, evidenceIds });
  }

  const candidates: ExitStrategyBodyV10['candidates'] = [];
  for (const [i, raw] of (arr(body.candidates, 'report.candidates', is) ?? []).entries()) {
    const path = `report.candidates.${i}`;
    const entry = rec(raw, path, is);
    if (!entry) continue;
    strict(entry, ['name', 'entityId', 'fitTier', 'dimensions', 'evidenceIds', 'counterargument', 'evidenceGaps'], path, is);
    const name = ident(entry.name, `${path}.name`, is);
    const entityId = ident(entry.entityId, `${path}.entityId`, is);
    const fitTier = oneOf(entry.fitTier, ACQUIRER_FIT_TIER_NAMES, `${path}.fitTier`, is);
    const evidenceIds = strArray(entry.evidenceIds, `${path}.evidenceIds`, is, 1);
    const counterargument = str(entry.counterargument, `${path}.counterargument`, is);
    const evidenceGaps = entry.evidenceGaps === undefined
      ? undefined
      : strArray(entry.evidenceGaps, `${path}.evidenceGaps`, is);
    const dimensions: ExitStrategyBodyV10['candidates'][number]['dimensions'] = [];
    for (const [j, rawDim] of (arr(entry.dimensions, `${path}.dimensions`, is) ?? []).entries()) {
      const dPath = `${path}.dimensions.${j}`;
      const dim = rec(rawDim, dPath, is);
      if (!dim) continue;
      strict(dim, ['dimension', 'supported', 'rationale', 'evidenceIds'], dPath, is);
      const dimension = oneOf(dim.dimension, ACQUIRER_FIT_DIMENSION_NAMES, `${dPath}.dimension`, is);
      const supported = bool(dim.supported, `${dPath}.supported`, is);
      const rationale = str(dim.rationale, `${dPath}.rationale`, is);
      const dimEvidence = strArray(dim.evidenceIds, `${dPath}.evidenceIds`, is);
      if (dimension === null || supported === null || rationale === null || dimEvidence === null) continue;
      dimensions.push({ dimension, supported, rationale, evidenceIds: dimEvidence });
    }
    if (
      name === null || entityId === null || fitTier === null || evidenceIds === null ||
      counterargument === null || evidenceGaps === null
    ) continue;
    candidates.push({
      name,
      entityId,
      fitTier,
      dimensions,
      evidenceIds,
      counterargument,
      ...(evidenceGaps === undefined ? {} : { evidenceGaps }),
    });
  }

  const comparables: ExitStrategyBodyV10['comparables'] = [];
  for (const [i, raw] of (arr(body.comparables, 'report.comparables', is) ?? []).entries()) {
    const path = `report.comparables.${i}`;
    const entry = rec(raw, path, is);
    if (!entry) continue;
    strict(entry, [
      'buyer',
      'target',
      'announcedAt',
      'rationale',
      'disclosedValue',
      'disclosedCurrency',
      'valueDisclosed',
      'evidenceIds',
    ], path, is);
    const buyer = str(entry.buyer, `${path}.buyer`, is);
    const target = str(entry.target, `${path}.target`, is);
    const announcedAt = str(entry.announcedAt, `${path}.announcedAt`, is);
    const rationale = optStr(entry.rationale, `${path}.rationale`, is);
    const disclosedValue = entry.disclosedValue === undefined
      ? undefined
      : fin(entry.disclosedValue, `${path}.disclosedValue`, is);
    const disclosedCurrency = optStr(entry.disclosedCurrency, `${path}.disclosedCurrency`, is);
    const valueDisclosed = bool(entry.valueDisclosed, `${path}.valueDisclosed`, is);
    const evidenceIds = strArray(entry.evidenceIds, `${path}.evidenceIds`, is, 1);
    if (
      buyer === null || target === null || announcedAt === null || rationale === null ||
      disclosedValue === null || disclosedCurrency === null || valueDisclosed === null || evidenceIds === null
    ) continue;
    comparables.push({
      buyer,
      target,
      announcedAt,
      ...(rationale === undefined ? {} : { rationale }),
      ...(disclosedValue === undefined ? {} : { disclosedValue }),
      ...(disclosedCurrency === undefined ? {} : { disclosedCurrency }),
      valueDisclosed,
      evidenceIds,
    });
  }

  if (
    thesisComparison === null || assessedThesisVersionId === null || mostSupportedPathways === null ||
    readinessGaps === null || milestones === null || insufficientReason === undefined
  ) return null;
  if (!is.ok) return null;
  return {
    pathways,
    mostSupportedPathways,
    candidates,
    comparables,
    readinessGaps,
    milestones,
    thesisComparison,
    assessedThesisVersionId,
    insufficientReason,
  };
}

// ===========================================================================
// The registry
// ===========================================================================

type Reader = (value: unknown, is: Issues) => unknown;

/**
 * Keyed by the EXACT `(schema_version, analysis_type)` pair.
 *
 * A `Map`, not an object literal: a plain-object lookup walks the prototype
 * chain, so `PARSERS['constructor']` returns a callable and sails past an
 * unsupported-version guard. `schema_version` is a database value, so that is
 * reachable from stored data.
 *
 * The version is matched RAW — no trim, no normalization. `' 1.1.0 '` is not a
 * version this code wrote, and repairing stored data by guessing is how a shape
 * nobody produced gets read as one we understand.
 */
const PARSERS = new Map<string, Reader>([
  ['1.0.0:competition', readCompetitionV10],
  ['1.0.0:porter', readPorterV10],
  ['1.0.0:pestel', readPestelV10],
  ['1.0.0:market_validation', readMarketV10],
  ['1.0.0:exit_strategy', readExitV10],
  ['1.1.0:competition', readCompetitionV11],
  ['1.1.0:porter', readPorterV11],
  ['1.1.0:pestel', readPestelV11],
  ['1.1.0:market_validation', readMarketV11],
  ['1.1.0:exit_strategy', readExitV11],
]);

/**
 * Parse one stored report against the contract for its own version.
 *
 * The three outcomes are the point. `unsupported_version` is a deployment
 * mismatch — a client older than the report it is reading — and the reader is
 * told to reload rather than shown an empty analysis. `malformed` is a
 * corruption worth reporting. Neither is `not_generated`, which is what the
 * previous guard collapsed both of them into.
 */
export function parseStoredReport(
  analysisType: AnalysisTypeName,
  schemaVersion: unknown,
  report: unknown,
): ReportParseResult<unknown> {
  const version = typeof schemaVersion === 'string' ? schemaVersion : '';
  const parser = PARSERS.get(`${version}:${analysisType}`);
  if (!parser) return { status: 'unsupported_version', schemaVersion: version };

  const is = new Issues();
  const body = parser(report, is);
  if (body === null || !is.ok) {
    return { status: 'malformed', schemaVersion: version, issues: is.list };
  }
  return { status: 'valid', schemaVersion: version, body };
}

/**
 * Validate a report the assembler just built, against the CURRENT version.
 *
 * Called before publication. A report that fails this must never be persisted
 * as `ready`: the alternative is a run that says "finished" over a body the
 * reader's own parser will then reject, which is exactly the state this feature
 * shipped in.
 */
export function validateCurrentReport(
  analysisType: AnalysisTypeName,
  report: unknown,
): ReportParseResult<unknown> {
  return parseStoredReport(analysisType, REPORT_SCHEMA_VERSION, report);
}

/** Whether a stored version has a reader at all. Exposed for status resolution. */
export function isSupportedReportVersion(schemaVersion: unknown, analysisType: AnalysisTypeName): boolean {
  return typeof schemaVersion === 'string' && PARSERS.has(`${schemaVersion}:${analysisType}`);
}

// ===========================================================================
// The CURRENT-version boundary
// ===========================================================================

/**
 * A parsed report, with its analysis type still attached to its body.
 *
 * THE CORRELATION IS THE POINT. `parseStoredReport` answers `unknown`, so every
 * consumer had to cast — `body as CompetitionBody`, `body as never` — and a cast
 * is exactly where the pairing of "which analysis" with "which shape" is lost.
 * A discriminated union carries it, so an exhaustive switch keeps the two
 * correlated and the compiler refuses a body handed to the wrong component.
 */
export type ReadyReport =
  | { analysisType: 'competition'; body: CompetitionBodyV11 }
  | { analysisType: 'porter'; body: PorterBodyV11 }
  | { analysisType: 'pestel'; body: PestelBodyV11 }
  | { analysisType: 'market_validation'; body: MarketValidationBodyV11 }
  | { analysisType: 'exit_strategy'; body: ExitStrategyBodyV11 };

export type CurrentReportParse =
  | { status: 'valid'; report: ReadyReport }
  /**
   * A version with a reader, but not THIS one.
   *
   * ITS OWN OUTCOME, not `valid`. A 1.0.0 body parsing successfully proves it is
   * a well-formed 1.0.0 body — it does not make it renderable by components that
   * read per-node `claimKey`s, identity provenance and grounded-statement
   * labels, none of which that version has. Casting it across would produce a
   * report with no citations anywhere and an "AI consideration" label missing
   * from statements that need one, which is worse than declining to draw it.
   */
  | { status: 'legacy'; schemaVersion: string; report: LegacyReport }
  | { status: 'unsupported_version'; schemaVersion: string }
  | { status: 'malformed'; schemaVersion: string; issues: string[] };

/**
 * Parse a stored report for DISPLAY.
 *
 * The only entry point a component tree should use. It refuses to hand back
 * anything the current components can render safely, and it never casts.
 */
/**
 * A 1.0.0 body, carried to the screen with its analysis type still attached.
 *
 * WHY IT IS A SEPARATE TYPE AND NOT AN UPGRADE. Twelve nodes in the 1.1.0
 * contract require a non-optional `claimKey`, and a 1.0.0 row has none — the
 * per-node keys, the identity provenance and the grounded-statement labels all
 * arrived with 1.1.0. Filling those in to reuse today's components would be
 * inventing twelve kinds of provenance for an old row, which is the one thing
 * this feature must never do. So the old body travels as ITSELF, is rendered by
 * a reader that offers no citation affordance at all, and is labelled as coming
 * from an older method.
 *
 * The alternative — refusing to show it — is what shipped, and it meant a
 * founder with published analyses saw nothing at all.
 */
export type LegacyReport =
  | { analysisType: 'competition'; body: CompetitionBodyV10 }
  | { analysisType: 'porter'; body: PorterBodyV10 }
  | { analysisType: 'pestel'; body: PestelBodyV10 }
  | { analysisType: 'market_validation'; body: MarketValidationBodyV10 }
  | { analysisType: 'exit_strategy'; body: ExitStrategyBodyV10 };

/** Same switch, same correlation, for the older contract. */
function legacyReport(analysisType: AnalysisTypeName, body: unknown): LegacyReport {
  switch (analysisType) {
    case 'competition':
      return { analysisType, body: body as CompetitionBodyV10 };
    case 'porter':
      return { analysisType, body: body as PorterBodyV10 };
    case 'pestel':
      return { analysisType, body: body as PestelBodyV10 };
    case 'market_validation':
      return { analysisType, body: body as MarketValidationBodyV10 };
    case 'exit_strategy':
      return { analysisType, body: body as ExitStrategyBodyV10 };
  }
}

export function parseReportForDisplay(
  analysisType: AnalysisTypeName,
  schemaVersion: unknown,
  report: unknown,
): CurrentReportParse {
  const version = typeof schemaVersion === 'string' ? schemaVersion : '';
  const parsed = parseStoredReport(analysisType, version, report);
  if (parsed.status === 'unsupported_version') {
    return { status: 'unsupported_version', schemaVersion: version };
  }
  if (parsed.status === 'malformed') {
    return { status: 'malformed', schemaVersion: version, issues: parsed.issues };
  }
  if (version !== REPORT_SCHEMA_VERSION) {
    // The body travels WITH the legacy verdict. It parsed — that is what makes
    // it legacy rather than corrupt — and withholding it here is what left the
    // screen with nothing to show for a published analysis.
    return { status: 'legacy', schemaVersion: version, report: legacyReport(analysisType, parsed.body) };
  }

  // The switch is what carries the correlation. Each arm narrows `body` to the
  // shape its own reader produced, so no cast is needed and none is possible.
  switch (analysisType) {
    case 'competition':
      return { status: 'valid', report: { analysisType, body: parsed.body as CompetitionBodyV11 } };
    case 'porter':
      return { status: 'valid', report: { analysisType, body: parsed.body as PorterBodyV11 } };
    case 'pestel':
      return { status: 'valid', report: { analysisType, body: parsed.body as PestelBodyV11 } };
    case 'market_validation':
      return {
        status: 'valid',
        report: { analysisType, body: parsed.body as MarketValidationBodyV11 },
      };
    case 'exit_strategy':
      return { status: 'valid', report: { analysisType, body: parsed.body as ExitStrategyBodyV11 } };
  }
}
